import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { validateManualInputImages, validateManualRequirementInput } from '../../utils/validators';
import { buildTestCaseComparisonMetrics } from '../../utils/testCaseComparison';
import { fetchPdfWorkflowState } from '../pdf/workflowHydration';
import { normalizePdfWorkflowStep } from '../pdf/workflowRoutes';
import { announceJob, listJobs, waitForJobResult } from '../../services/jobs';

const SUPPORTED_BRD_EXTENSIONS = new Set(['pdf', 'doc', 'docx']);
const SUPPORTED_OPENAPI_EXTENSIONS = new Set(['json', 'yaml', 'yml']);
const SUPPORTED_EXCEL_EXTENSIONS = new Set(['xlsx']);
const MAX_EXCEL_IMPORT_BYTES = 10 * 1024 * 1024;
const SUPPORTED_BRD_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.ms-word',
  'application/x-msword',
  'application/word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/octet-stream',
  'application/zip',
]);
const SUPPORTED_OPENAPI_TYPES = new Set([
  'application/json',
  'application/yaml',
  'application/x-yaml',
  'text/yaml',
  'text/x-yaml',
  'text/plain',
  'application/octet-stream',
  '',
]);
const SUPPORTED_EXCEL_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/octet-stream',
  'application/zip',
  '',
]);

const getFileExtension = (fileName = '') => {
  const parts = String(fileName || '').toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const isSupportedBrdFile = (file) => {
  if (!file) return false;
  const extension = getFileExtension(file.name);
  if (!SUPPORTED_BRD_EXTENSIONS.has(extension)) return false;
  const fileType = String(file.type || '').trim().toLowerCase();
  return !fileType || SUPPORTED_BRD_TYPES.has(fileType);
};

const isSupportedOpenApiFile = (file) => {
  if (!file) return false;
  const extension = getFileExtension(file.name);
  if (!SUPPORTED_OPENAPI_EXTENSIONS.has(extension)) return false;
  const fileType = String(file.type || '').trim().toLowerCase();
  return SUPPORTED_OPENAPI_TYPES.has(fileType);
};

const isSupportedExcelFile = (file) => {
  if (!file) return false;
  const extension = getFileExtension(file.name);
  if (!SUPPORTED_EXCEL_EXTENSIONS.has(extension)) return false;
  const fileType = String(file.type || '').trim().toLowerCase();
  return SUPPORTED_EXCEL_TYPES.has(fileType);
};

const DOCUMENT_NAME_KEYS = [
  'pdf_file',
  'document_name',
  'file_name',
  'filename',
  'title',
  'name',
];

const PDF_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HISTORY_LOADED_BRD_SESSION_KEY = 'history_loaded_brd_id';

const getSessionStorage = () => (
  typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null
);

const rememberHistoryLoadedBrd = (pdfId) => {
  const normalizedPdfId = String(pdfId || '').trim();
  const storage = getSessionStorage();
  if (!storage || !normalizedPdfId) return;
  storage.setItem(HISTORY_LOADED_BRD_SESSION_KEY, normalizedPdfId);
};

const clearHistoryLoadedBrd = (pdfId = null) => {
  const storage = getSessionStorage();
  if (!storage) return;
  const normalizedPdfId = String(pdfId || '').trim();
  const currentPdfId = String(storage.getItem(HISTORY_LOADED_BRD_SESSION_KEY) || '').trim();
  if (!normalizedPdfId || !currentPdfId || currentPdfId === normalizedPdfId) {
    storage.removeItem(HISTORY_LOADED_BRD_SESSION_KEY);
  }
};

const wasHistoryLoadedBrd = (pdfId) => {
  const normalizedPdfId = String(pdfId || '').trim();
  const storage = getSessionStorage();
  return Boolean(
    storage &&
    normalizedPdfId &&
    String(storage.getItem(HISTORY_LOADED_BRD_SESSION_KEY) || '').trim() === normalizedPdfId
  );
};

// The PDF snapshot was applied but the routed section has no output to open,
// so callers can land on section selection instead of an empty step.
const sectionOutputUnavailableError = (message) => Object.assign(new Error(message), { pdfRestored: true });

const isPendingBrdUpdatePayload = (value) => {
  const summary =
    value?.change_summary
    || value?.changeAnalysis?.summary
    || value?.change_analysis?.summary
    || value?.summary
    || {};
  return Boolean(
    value?.brd_update_pending
    || value?.brdUpdatePending
    || summary?.status === 'pending_extraction'
  );
};

const isTocStructureReviewRequired = (changeAnalysis = null) => {
  if (!changeAnalysis || typeof changeAnalysis !== 'object') return false;
  const comparison = changeAnalysis.comparison && typeof changeAnalysis.comparison === 'object'
    ? changeAnalysis.comparison
    : {};
  const summary = changeAnalysis.summary && typeof changeAnalysis.summary === 'object'
    ? changeAnalysis.summary
    : comparison.summary && typeof comparison.summary === 'object'
      ? comparison.summary
      : {};
  const structureHealth = comparison.structure_health && typeof comparison.structure_health === 'object'
    ? comparison.structure_health
    : {};
  const comparisonStatus = String(comparison.status || '').trim().toLowerCase();
  const structureStatus = String(structureHealth.status || summary.structure_status || '').trim().toLowerCase();
  const structureApproved = Boolean(
    changeAnalysis.toc_structure_review?.approved
    || changeAnalysis.review_decisions?.toc_structure_review?.approved
  );
  return Boolean(
    !structureApproved
    && (comparisonStatus === 'structure_changed' || structureStatus === 'blocked')
  );
};

const normalizeDocumentName = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized || PDF_ID_PATTERN.test(normalized)) return '';
  return normalized;
};

const resolveDocumentName = (...sources) => {
  for (const source of sources) {
    if (!source) continue;
    if (typeof source === 'string') {
      const candidate = normalizeDocumentName(source);
      if (candidate) return candidate;
      continue;
    }
    if (typeof source !== 'object') continue;
    for (const key of DOCUMENT_NAME_KEYS) {
      const candidate = normalizeDocumentName(source[key]);
      if (candidate) return candidate;
    }
  }
  return '';
};

const SECTION_BUILD_ERROR_MESSAGE =
  "We couldn't build sections from this BRD. Try again, or use Find Features if it keeps happening.";

const TECHNICAL_ERROR_PATTERNS = [
  /\bLLM\b/i,
  /\bGemini\b/i,
  /\bClaude\b/i,
  /\bAnthropic\b/i,
  /\bAPI key\b/i,
  /\bconfiguration\b/i,
  /\brow[_\s-]?id\b/i,
  /\bsource_row_id\b/i,
  /\bincluded_row_ids\b/i,
  /\bdescendant\b/i,
  /\bgeneration target\b/i,
  /\bnon-descendant\b/i,
  /\bChromaDB\b/i,
  /\bcollection\b/i,
  /\bpypdf\b/i,
  /\btraceback\b/i,
  /\bstack\b/i,
  /\bschema\b/i,
  /\bImportError\b/i,
  /\bAttributeError\b/i,
  /\bTypeError\b/i,
  /\bValueError\b/i,
  /\bValidationError\b/i,
  /\bHTTPException\b/i,
  /\bDetails:/i,
  /\br\d+\b/i,
];

const toUserFacingApiError = (message, fallbackMessage) => {
  const normalized = String(message || '').trim();
  if (!normalized) return fallbackMessage;
  if (/\b(queue|queued|worker|bulk)\b/i.test(normalized)) {
    return fallbackMessage;
  }
  if (TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return fallbackMessage;
  }
  return normalized;
};

const STREAM_CASE_CATEGORIES = ['positive', 'negative'];

const resolveStreamCategory = (value, fallback = null) => {
  const normalized = String(value || '').trim().toLowerCase();
  return STREAM_CASE_CATEGORIES.includes(normalized) ? normalized : fallback;
};

const countStreamCases = (testCases = {}) =>
  STREAM_CASE_CATEGORIES.reduce(
    (total, category) => total + (Array.isArray(testCases?.[category]) ? testCases[category].length : 0),
    0
  );

const countStreamCasesWithInputSets = (testCases = {}) =>
  STREAM_CASE_CATEGORIES.reduce((total, category) => {
    const cases = Array.isArray(testCases?.[category]) ? testCases[category] : [];
    return total + cases.filter((testCase) => Array.isArray(testCase?.user_input_sets) && testCase.user_input_sets.length > 0).length;
  }, 0);

const updateStreamCaseCounts = (testCases = {}) => {
  const positive = Array.isArray(testCases.positive) ? testCases.positive : [];
  const negative = Array.isArray(testCases.negative) ? testCases.negative : [];
  return {
    ...testCases,
    positive,
    negative,
    positive_count: positive.length,
    negative_count: negative.length,
    total_count: positive.length + negative.length,
  };
};

const AUTHORITATIVE_INPUT_SOURCE_TYPES = new Set([
  'manual_upload',
  'manual_uploaded',
  'user_imported',
  'testcase_excel',
  'test_case_excel',
  'excel_test_cases',
]);

const hasInputRows = (inputSet) => (
  Array.isArray(inputSet?.user_inputs || inputSet?.inputs || inputSet?.values)
  && (inputSet.user_inputs || inputSet.inputs || inputSet.values).some((item) => (
    item && typeof item === 'object' && String(item.field || item.name || item.label || '').trim()
  ))
);

const hasAuthoritativeInputSets = (testCase = {}) => {
  const inputSets = Array.isArray(testCase?.user_input_sets) ? testCase.user_input_sets : [];
  if (!inputSets.some(hasInputRows)) return false;
  const source = testCase?.source && typeof testCase.source === 'object' ? testCase.source : {};
  const sourceType = String(testCase?.source_type || source.source_type || '').trim().toLowerCase();
  if (AUTHORITATIVE_INPUT_SOURCE_TYPES.has(sourceType)) return true;
  if (String(testCase?.source_import_id || source.source_import_id || source.import_id || '').trim()) return true;
  if (String(testCase?.input_values_updated_at || '').trim()) return true;
  return inputSets.some((item) => String(item?.set_name || '').trim().toLowerCase() === 'imported test data');
};

const buildInitialStreamTestCases = (sectionTitle, fieldConfig = null) => ({
  module: sectionTitle || 'Test Cases',
  positive: [],
  negative: [],
  positive_count: 0,
  negative_count: 0,
  total_count: 0,
  enrichment_status: 'pending',
  input_sets_status: 'pending',
  gap_analysis_status: 'pending',
  ...(fieldConfig ? { field_config: fieldConfig } : {}),
});

const mergeStreamCaseIntoResult = (currentResult, payload = {}) => {
  if (!currentResult || typeof currentResult !== 'object') return currentResult;
  const category = resolveStreamCategory(payload.category || payload.prompt_type, 'positive');
  const incomingCase = payload.test_case && typeof payload.test_case === 'object'
    ? payload.test_case
    : null;
  if (!incomingCase) return currentResult;

  const testCases = currentResult.test_cases && typeof currentResult.test_cases === 'object'
    ? currentResult.test_cases
    : buildInitialStreamTestCases(currentResult.section_title, currentResult.test_case_field_config);
  const currentCases = Array.isArray(testCases[category]) ? testCases[category] : [];
  const incomingId = String(incomingCase.test_case_no || '').trim();
  const nextCase = {
    ...incomingCase,
    _streaming: true,
  };
  const nextCases = [...currentCases];
  const existingIndex = incomingId
    ? nextCases.findIndex((testCase) => String(testCase?.test_case_no || '').trim() === incomingId)
    : -1;

  if (existingIndex >= 0) {
    const existingCase = nextCases[existingIndex] || {};
    const preserveInputSets = hasAuthoritativeInputSets(existingCase);
    nextCases[existingIndex] = {
      ...existingCase,
      ...nextCase,
      user_inputs: Array.isArray(nextCase.user_inputs) && nextCase.user_inputs.length > 0
        && !preserveInputSets
        ? nextCase.user_inputs
        : existingCase.user_inputs,
      user_input_sets: Array.isArray(nextCase.user_input_sets) && nextCase.user_input_sets.length > 0
        && !preserveInputSets
        ? nextCase.user_input_sets
        : existingCase.user_input_sets,
      _streaming: true,
      _streaming_input_sets: existingCase._streaming_input_sets,
    };
  } else {
    nextCases.push(nextCase);
  }

  const nextTestCases = updateStreamCaseCounts({
    ...testCases,
    [category]: nextCases,
  });
  const visibleCaseCount = countStreamCases(nextTestCases);

  return {
    ...currentResult,
    status: 'running',
    stream_status: 'running',
    test_cases: nextTestCases,
    streamed_case_count: Math.max(Number(currentResult.streamed_case_count || 0), visibleCaseCount),
  };
};

const mergeStreamInputSetsIntoResult = (currentResult, payload = {}) => {
  if (!currentResult || typeof currentResult !== 'object') return currentResult;
  const caseId = String(payload.test_case_no || '').trim();
  if (!caseId) return currentResult;

  const testCases = currentResult.test_cases && typeof currentResult.test_cases === 'object'
    ? currentResult.test_cases
    : buildInitialStreamTestCases(currentResult.section_title, currentResult.test_case_field_config);
  const preferredCategory = resolveStreamCategory(payload.category || payload.prompt_type);
  const categories = preferredCategory
    ? [preferredCategory, ...STREAM_CASE_CATEGORIES.filter((category) => category !== preferredCategory)]
    : STREAM_CASE_CATEGORIES;
  const incomingSets = Array.isArray(payload.user_input_sets) ? payload.user_input_sets : [];
  if (incomingSets.length === 0) return currentResult;
  const firstInputSet = incomingSets.find((item) => Array.isArray(item?.user_inputs));
  let didMerge = false;
  let nextTestCases = { ...testCases };

  categories.forEach((category) => {
    if (didMerge) return;
    const currentCases = Array.isArray(testCases[category]) ? testCases[category] : [];
    const existingIndex = currentCases.findIndex(
      (testCase) => String(testCase?.test_case_no || '').trim() === caseId
    );
    if (existingIndex < 0) return;
    const nextCases = [...currentCases];
    const existingCase = nextCases[existingIndex] || {};
    if (hasAuthoritativeInputSets(existingCase)) {
      didMerge = true;
      return;
    }
    nextCases[existingIndex] = {
      ...existingCase,
      user_inputs: Array.isArray(payload.user_inputs) && payload.user_inputs.length > 0
        ? payload.user_inputs
        : (Array.isArray(firstInputSet?.user_inputs) ? firstInputSet.user_inputs : existingCase.user_inputs),
      user_input_sets: incomingSets,
      _streaming: true,
      _streaming_input_sets: true,
    };
    nextTestCases = {
      ...nextTestCases,
      [category]: nextCases,
    };
    didMerge = true;
  });

  const countedInputSets = didMerge
    ? countStreamCasesWithInputSets(nextTestCases)
    : Number(currentResult.streamed_input_sets_count || 0);

  return {
    ...currentResult,
    stream_status: 'running',
    test_cases: updateStreamCaseCounts(nextTestCases),
    streamed_input_sets_count: Math.max(
      Number(currentResult.streamed_input_sets_count || 0),
      countedInputSets,
      didMerge ? 1 : 0
    ),
  };
};

const updateStreamEnrichmentStatus = (currentResult, status, payload = {}) => {
  if (!currentResult || typeof currentResult !== 'object') return currentResult;
  const testCases = currentResult.test_cases && typeof currentResult.test_cases === 'object'
    ? currentResult.test_cases
    : buildInitialStreamTestCases(currentResult.section_title, currentResult.test_case_field_config);

  return {
    ...currentResult,
    stream_status: status === 'failed' ? 'failed' : 'running',
    enrichment_status: status,
    input_sets_status: status,
    gap_analysis_status: status,
    coverage_gaps: Array.isArray(payload.coverage_gaps) ? payload.coverage_gaps : currentResult.coverage_gaps,
    suggested_additional: payload.suggested_additional || currentResult.suggested_additional,
    enriched_at: payload.enriched_at || currentResult.enriched_at,
    test_cases: {
      ...testCases,
      enrichment_status: status,
      input_sets_status: status,
      gap_analysis_status: status,
      coverage_gaps: Array.isArray(payload.coverage_gaps) ? payload.coverage_gaps : testCases.coverage_gaps,
      suggested_additional: payload.suggested_additional || testCases.suggested_additional,
      enriched_at: payload.enriched_at || testCases.enriched_at,
    },
  };
};

const markStreamResultFailed = (currentResult, message) => {
  if (!currentResult || typeof currentResult !== 'object') return currentResult;
  return {
    ...updateStreamEnrichmentStatus(currentResult, 'failed'),
    status: 'failed',
    stream_status: 'failed',
    stream_error: message || 'Generation stopped before completion.',
  };
};

const parseSseEventBlock = (block) => {
  const eventLines = String(block || '').split('\n');
  let event = 'message';
  const dataLines = [];

  eventLines.forEach((line) => {
    if (!line || line.startsWith(':')) return;
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim() || 'message';
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
    }
  });

  const rawData = dataLines.join('\n').trim();
  if (!rawData && event === 'message') return null;

  let data = {};
  if (rawData) {
    try {
      data = JSON.parse(rawData);
    } catch (_err) {
      data = { raw: rawData };
    }
  }

  return { event, data };
};

const readSseResponse = async (response, onEvent) => {
  const reader = response.body?.getReader?.();
  if (!reader) {
    throw new Error('Streaming responses are not available in this browser.');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const normalizedBuffer = buffer.replace(/\r\n/g, '\n');
      const blocks = normalizedBuffer.split('\n\n');
      buffer = blocks.pop() || '';

      for (const block of blocks) {
        const parsed = parseSseEventBlock(block.trim());
        if (parsed) {
          await onEvent(parsed);
        }
      }

      if (done) break;
    }

    const remaining = buffer.trim();
    if (remaining) {
      const parsed = parseSseEventBlock(remaining.replace(/\r\n/g, '\n'));
      if (parsed) {
        await onEvent(parsed);
      }
    }
  } finally {
    reader.releaseLock?.();
  }
};

export const usePDFWorkflow = (currentUser = null, options = {}) => {
  const intakeMode = String(options?.intakeMode || 'document').trim().toLowerCase();
  const lockedApplicationId = String(options?.applicationId || '').trim();
  const applicationLocked = Boolean(lockedApplicationId);
  const historySourceCategory = intakeMode === 'text'
    ? 'requirements'
    : intakeMode === 'api'
      ? 'openapi'
      : intakeMode === 'excel'
        ? 'testcase_excel'
        : 'brd';
  const historySourceLabel = historySourceCategory === 'requirements'
    ? 'requirement source'
    : historySourceCategory === 'openapi'
      ? 'API contract'
      : historySourceCategory === 'testcase_excel'
        ? 'Excel testcase source'
        : 'BRD';
  const historySourceListLabel = historySourceCategory === 'requirements'
    ? 'requirement sources'
    : historySourceCategory === 'openapi'
      ? 'API contracts'
      : historySourceCategory === 'testcase_excel'
        ? 'Excel testcase sources'
        : 'BRDs';
  const workflowRouteRoot = historySourceCategory === 'requirements'
    ? 'requirements'
    : historySourceCategory === 'openapi'
      ? 'api-testing'
      : historySourceCategory === 'testcase_excel'
        ? 'excel-testcases'
        : 'pdf';
  const applicationSessionSuffix = applicationLocked ? `:${lockedApplicationId}` : '';
  const activePdfSessionKey = `active_pdf_id:${workflowRouteRoot}${applicationSessionSuffix}`;
  const workflowLastRouteSessionKey = `testsuite_workflow_last_route:${workflowRouteRoot}${applicationSessionSuffix}`;
  const excelImportSessionKey = `testsuite_excel_import:${workflowRouteRoot}${applicationSessionSuffix}`;
  const [file, setFile] = useState(null);
  const [openApiFile, setOpenApiFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [excelImportAnalysis, setExcelImportAnalysis] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [manualInputTitle, setManualInputTitle] = useState('');
  const [manualInputText, setManualInputText] = useState('');
  const [manualInputImages, setManualInputImages] = useState([]);
  const [pdfId, setPdfId] = useState(null);
  const [sections, setSections] = useState([]);
  const [rawSectionOutline, setRawSectionOutline] = useState([]);
  const [sectionGrouping, setSectionGrouping] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [status, setStatus] = useState('idle');
  const statusRef = useRef(status);
  statusRef.current = status;
  const extractionJobIdRef = useRef('');
  const [activeUploadSource, setActiveUploadSource] = useState(null);
  const [error, setError] = useState(null);
  const [processingResult, setProcessingResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  // Read by async restores that must not move a workflow that has since been
  // hydrated (or is being hydrated) to a later step from a route snapshot.
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  const hasSectionsRef = useRef(false);
  hasSectionsRef.current = Array.isArray(sections) && sections.length > 0;
  const pdfHydrationsInFlightRef = useRef(0);
  // Set while startBulkGeneration's POST is pending. The route moves to the
  // generation step before the job exists, so a route-driven restore would
  // otherwise report "no job found" and clobber the job being created.
  const bulkStartInFlightRef = useRef(false);
  // Selected automation target: 'web' (Playwright), 'mobile' (local Appium
  // emulator) or 'browserstack' (cloud device). Drives the POM/script path.
  const [testingType, setTestingType] = useState('web');
  const [isReprocessed, setIsReprocessed] = useState(false);
  const [showPreviousPDFs, setShowPreviousPDFs] = useState(false);
  const [previousPDFs, setPreviousPDFs] = useState([]);
  const [previousPDFsLoading, setPreviousPDFsLoading] = useState(false);
  const [previousPDFsError, setPreviousPDFsError] = useState(null);
  const [historyRestoreLoading, setHistoryRestoreLoading] = useState(null);
  const [selectedPreviousPdf, setSelectedPreviousPdf] = useState(null);
  const [dashboardPdf, setDashboardPdf] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [sectionArtifactIndex, setSectionArtifactIndex] = useState({});
  const [historyScriptsByCaseId, setHistoryScriptsByCaseId] = useState({});
  const [processingMode, setProcessingMode] = useState('pdf-only');
  const [editedTestCases, setEditedTestCases] = useState([]);
  const [originalTestCases, setOriginalTestCases] = useState([]);
  const [apiResponse, setApiResponse] = useState(null);
  const [runResults, setRunResults] = useState({});
  const [preAnalysisPlan, setPreAnalysisPlan] = useState(null);
  const [approvedCounts, setApprovedCounts] = useState({ positive_count: null, negative_count: null });
  const [approvedFocus, setApprovedFocus] = useState([]);
  const [approvedGlobalConstraints, setApprovedGlobalConstraints] = useState([]);
  const [approvedTestCaseFieldConfig, setApprovedTestCaseFieldConfig] = useState(null);
  const [dgsConfig, setDgsConfig] = useState(null);
  const [automationBaseUrl, setAutomationBaseUrl] = useState('');
  const [automationBaseUrlLoading, setAutomationBaseUrlLoading] = useState(false);
  const [automationBaseUrlSaving, setAutomationBaseUrlSaving] = useState(false);
  const [automationBaseUrlError, setAutomationBaseUrlError] = useState('');
  const [confirmedReplacementSection, setConfirmedReplacementSection] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceIdState] = useState(lockedApplicationId);
  const setSelectedWorkspaceId = useCallback((nextValue) => {
    setSelectedWorkspaceIdState((currentValue) => {
      if (applicationLocked) return lockedApplicationId;
      return typeof nextValue === 'function' ? nextValue(currentValue) : nextValue;
    });
  }, [applicationLocked, lockedApplicationId]);
  useEffect(() => {
    if (applicationLocked) {
      setSelectedWorkspaceIdState(lockedApplicationId);
    }
  }, [applicationLocked, lockedApplicationId]);
  const requireMatchingApplication = useCallback((candidateApplicationId = '') => {
    const normalizedCandidate = String(candidateApplicationId || '').trim();
    if (
      applicationLocked
      && normalizedCandidate
      && normalizedCandidate !== lockedApplicationId
    ) {
      throw new Error('This source belongs to a different application.');
    }
    return applicationLocked ? lockedApplicationId : normalizedCandidate;
  }, [applicationLocked, lockedApplicationId]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [workspaceMembersLoading, setWorkspaceMembersLoading] = useState(false);
  const [workspaceMembersError, setWorkspaceMembersError] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeUsersLoading, setActiveUsersLoading] = useState(false);
  const [activeUsersError, setActiveUsersError] = useState('');
  const [showWorkspaceMembersModal, setShowWorkspaceMembersModal] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [approvalEvents, setApprovalEvents] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingApprovalsLoading, setPendingApprovalsLoading] = useState(false);
  const [brdSharingStatus, setBrdSharingStatus] = useState(null);
  const [brdSharingRequests, setBrdSharingRequests] = useState([]);
  const [brdSharingLoading, setBrdSharingLoading] = useState(false);
  const [testCaseApprovalStatus, setTestCaseApprovalStatus] = useState(null);
  const [testCaseApprovalEvents, setTestCaseApprovalEvents] = useState([]);
  const [testCaseApprovalLoading, setTestCaseApprovalLoading] = useState(false);
  const [bulkMode, setBulkMode] = useState('single');
  const [bulkSelectedSections, setBulkSelectedSections] = useState([]);
  const [bulkJob, setBulkJob] = useState(null);

  useEffect(() => {
    if (bulkJob?.id) announceJob(bulkJob, 'bulk');
  }, [bulkJob]);
  const [bulkItems, setBulkItems] = useState([]);
  const [bulkEvents, setBulkEvents] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [queuedSingleGeneration, setQueuedSingleGeneration] = useState(null);
  const [queuedPreAnalysis, setQueuedPreAnalysis] = useState(null);
  const [pdfExtractionMode, setPdfExtractionMode] = useState('auto');
  const [brdLoadedFromHistory, setBrdLoadedFromHistory] = useState(false);
  const [brdChangeAnalysis, setBrdChangeAnalysis] = useState(null);
  const [brdUpdatePending, setBrdUpdatePending] = useState(false);
  const [reusableSections, setReusableSections] = useState({
    available: false,
    stale: false,
    count: 0,
    extractionMethod: '',
    itemType: '',
  });
  const terminalBulkStatuses = useMemo(
    () => new Set(['completed', 'failed', 'cancelled', 'partial_completed']),
    []
  );
  const isOpenApiSource = useMemo(() => (
    (Array.isArray(sections) ? sections : []).some((section) => section?.source_type === 'openapi_operation')
    || String(selectedPreviousPdf?.source_type || selectedPreviousPdf?.item_type || '').trim().toLowerCase() === 'openapi'
    || Boolean(openApiFile)
  ), [openApiFile, sections, selectedPreviousPdf]);
  const isExcelSource = useMemo(() => {
    const excelSourceTypes = new Set(['testcase_excel', 'test_case_excel', 'excel_test_cases']);
    return (
      (Array.isArray(sections) ? sections : []).some((section) => (
        excelSourceTypes.has(String(section?.source_type || '').trim().toLowerCase())
      ))
      || excelSourceTypes.has(
        String(selectedPreviousPdf?.source_type || selectedPreviousPdf?.item_type || '').trim().toLowerCase()
      )
      || excelSourceTypes.has(
        String(processingResult?.source_type || processingResult?.item_type || '').trim().toLowerCase()
      )
    );
  }, [processingResult, sections, selectedPreviousPdf]);

  useEffect(() => {
    const storage = getSessionStorage();
    if (!storage) return;
    if (pdfId) {
      storage.setItem('active_pdf_id', pdfId);
      storage.setItem(activePdfSessionKey, pdfId);
    } else {
      storage.removeItem('active_pdf_id');
      storage.removeItem(activePdfSessionKey);
    }
  }, [activePdfSessionKey, pdfId]);

  useEffect(() => {
    if (pdfId && wasHistoryLoadedBrd(pdfId)) {
      setBrdLoadedFromHistory(true);
    }
  }, [pdfId]);

  useEffect(() => {
    const validNumbers = new Set(
      (Array.isArray(sections) ? sections : [])
        .map((section) => Number(section?.number))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
    setBulkSelectedSections((prev) =>
      (Array.isArray(prev) ? prev : []).filter((value) => validNumbers.has(Number(value)))
    );
  }, [sections]);

  useEffect(() => {
    if (bulkMode !== 'single') return;
    const normalizedSection = Number(selectedSection);
    if (!Number.isFinite(normalizedSection) || normalizedSection <= 0) {
      setBulkSelectedSections([]);
      return;
    }
    setBulkSelectedSections([normalizedSection]);
  }, [bulkMode, selectedSection]);

  const hasCategorizedTestCases = (testCases) =>
    testCases &&
    typeof testCases === 'object' &&
    !Array.isArray(testCases) &&
    ('positive' in testCases || 'negative' in testCases);

  const getTestCaseCount = (testCases) => {
    if (!testCases) return 0;
    if (Array.isArray(testCases)) return testCases.length;
    if (hasCategorizedTestCases(testCases)) {
      const positiveCount = Array.isArray(testCases.positive) ? testCases.positive.length : 0;
      const negativeCount = Array.isArray(testCases.negative) ? testCases.negative.length : 0;
      return positiveCount + negativeCount;
    }
    return 1;
  };

  const toCaseArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return Object.values(value);
    return [];
  };

  const parseApiError = useCallback(async (response, fallbackMessage) => {
    const payload = await response.json().catch(() => ({}));
    const cleanMessage = (message) => {
      return toUserFacingApiError(message, fallbackMessage);
    };
    if (typeof payload?.detail === 'string') {
      return cleanMessage(payload.detail);
    }
    if (payload?.detail && typeof payload.detail === 'object' && !Array.isArray(payload.detail)) {
      return cleanMessage(payload.detail.message || payload.detail.detail || fallbackMessage);
    }
    if (Array.isArray(payload?.detail)) {
      const detail = payload.detail
        .map((item) => {
          const location = Array.isArray(item?.loc) ? item.loc.filter(Boolean).join('.') : '';
          const message = String(item?.msg || '').trim();
          return [location, message].filter(Boolean).join(': ');
        })
        .filter(Boolean)
        .join('; ');
      return cleanMessage(detail);
    }
    return fallbackMessage;
  }, []);

  const applySectionsResponse = useCallback((data = {}) => {
    const nextSections = Array.isArray(data?.sections) ? data.sections : [];
    setSections(nextSections);
    setRawSectionOutline(Array.isArray(data?.raw_section_outline) ? data.raw_section_outline : []);
    setSectionGrouping(
      data?.section_grouping && typeof data.section_grouping === 'object'
        ? data.section_grouping
        : null
    );
    return nextSections;
  }, []);

  const normalizeUniqueStringList = useCallback((values) => {
    const items = Array.isArray(values) ? values : [];
    const normalized = [];
    const seen = new Set();
    items.forEach((value) => {
      const item = String(value || '').trim();
      if (!item) return;
      const key = item.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      normalized.push(item);
    });
    return normalized;
  }, []);

  const normalizeBulkStatus = useCallback((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return 'queued';
    return normalized;
  }, []);

  const isBulkJobTerminal = useCallback((value) => {
    const normalized = normalizeBulkStatus(value);
    return terminalBulkStatuses.has(normalized);
  }, [normalizeBulkStatus, terminalBulkStatuses]);

  const normalizeApprovalState = useCallback((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'pending_approval') return 'pending_approval';
    if (normalized === 'approved') return 'approved';
    if (normalized === 'rejected') return 'rejected';
    return 'uploaded';
  }, []);

  const normalizeTestCaseApprovalState = useCallback((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'pending_review') return 'pending_review';
    if (normalized === 'approved') return 'approved';
    if (normalized === 'changes_requested') return 'changes_requested';
    return 'draft';
  }, []);

  const normalizeHistoryTestCasesPayload = (rawPayload) => {
    const source =
      rawPayload &&
      typeof rawPayload === 'object' &&
      rawPayload.test_cases &&
      typeof rawPayload.test_cases === 'object'
        ? rawPayload.test_cases
        : rawPayload;

    if (!source) return null;

    if (Array.isArray(source)) {
      return source;
    }

    if (hasCategorizedTestCases(source)) {
      const positive = toCaseArray(source.positive);
      const negative = toCaseArray(source.negative);
      return {
        positive,
        negative,
        positive_count: Number(source.positive_count ?? positive.length),
        negative_count: Number(source.negative_count ?? negative.length),
        total_count: Number(source.total_count ?? (positive.length + negative.length)),
        enrichment_status: source.enrichment_status || null,
        input_sets_status: source.input_sets_status || null,
        gap_analysis_status: source.gap_analysis_status || null,
        enriched_at: source.enriched_at || null,
        coverage_gaps: Array.isArray(source.coverage_gaps) ? source.coverage_gaps : [],
        suggested_additional: source.suggested_additional || null,
      };
    }

    if (typeof source === 'object') {
      return [source];
    }

    return null;
  };

  const buildSectionArtifactIndex = (dashboardPayload) => {
    const entries = {};
    const rows = Array.isArray(dashboardPayload?.sections) ? dashboardPayload.sections : [];
    rows.forEach((section) => {
      const sectionNumber = Number(section?.section_number);
      if (!Number.isFinite(sectionNumber)) return;
      const testCaseVersions = Number(section?.test_cases?.count || 0);
      const scriptVersions = Number(section?.scripts?.count || 0);
      entries[sectionNumber] = {
        section_number: sectionNumber,
        section_title: section?.section_title || `Section ${sectionNumber}`,
        test_case_versions: testCaseVersions,
        test_case_version_details: Array.isArray(section?.test_cases?.version_details)
          ? section.test_cases.version_details
          : [],
        active_test_case_version: section?.test_cases?.active_version || null,
        test_case_validity: section?.test_cases?.validity || null,
        script_versions: scriptVersions,
        has_generated: testCaseVersions > 0,
        active_test_case_count: Number(section?.test_cases?.preview?.total_count || 0),
        has_active_script: Boolean(section?.scripts?.preview?.has_script),
      };
    });
    // Analyzed sections whose plan has not been turned into test cases yet.
    // A plan-only section gets an entry without generated output. A section
    // that already has test cases opens those instead; a leftover plan (e.g.
    // from a cancelled regeneration) is not offered next to them.
    const plans = Array.isArray(dashboardPayload?.pre_analysis_plans) ? dashboardPayload.pre_analysis_plans : [];
    plans.forEach((plan) => {
      const sectionNumber = Number(plan?.section_number);
      if (!Number.isFinite(sectionNumber) || !plan?.pending) return;
      if (entries[sectionNumber]?.has_generated) return;
      entries[sectionNumber] = {
        section_number: sectionNumber,
        has_generated: false,
        test_case_versions: 0,
        script_versions: 0,
        ...entries[sectionNumber],
        pending_plan: {
          version: plan.version ?? null,
          created_at: plan.created_at || null,
          suggested: plan.suggested || {},
        },
      };
    });
    return entries;
  };

  const buildHistoryScriptsMap = (scriptsData) => {
    const scriptsByCaseId = {};
    const latestByCase = scriptsData?.latest_by_test_case;
    const draftsByCase = scriptsData?.drafts_by_test_case;

    if (latestByCase && typeof latestByCase === 'object') {
      Object.entries(latestByCase).forEach(([caseId, artifact]) => {
        if (!caseId) return;
        const script = String(artifact?.playwright_script || artifact?.script || '').trim();
        if (!script) return;
        scriptsByCaseId[String(caseId)] = {
          playwright_script: script,
          assumptions: Array.isArray(artifact?.assumptions) ? artifact.assumptions : [],
          warnings: Array.isArray(artifact?.warnings) ? artifact.warnings : [],
          model: artifact?.model || null,
          version: artifact?.version ?? null,
          status: artifact?.status || null,
          updated_at: artifact?.updated_at || null,
          draft_updated_at: null,
          requires_regeneration: Boolean(artifact?.requires_regeneration),
          stale_reason: artifact?.stale_reason || null,
          missing_poms: Array.isArray(artifact?.missing_poms) ? artifact.missing_poms : [],
          edited_by: artifact?.edited_by || null,
          edited_by_name: artifact?.edited_by_name || null,
          edited_by_email: artifact?.edited_by_email || null,
        };
      });
    }

    if (draftsByCase && typeof draftsByCase === 'object') {
      Object.entries(draftsByCase).forEach(([caseId, artifact]) => {
        if (!caseId) return;
        const draftScript = String(artifact?.playwright_script || artifact?.script || '').trim();
        if (!draftScript) return;

        const current = scriptsByCaseId[String(caseId)] || {
          playwright_script: '',
          assumptions: [],
          warnings: [],
          model: null,
          version: null,
          status: null,
          updated_at: null,
          draft_updated_at: null,
          requires_regeneration: false,
          stale_reason: null,
          missing_poms: [],
          edited_by: null,
          edited_by_name: null,
          edited_by_email: null,
        };
        const savedUpdatedAt = current.updated_at ? Date.parse(current.updated_at) : Number.NaN;
        const draftUpdatedAt = artifact?.updated_at ? Date.parse(artifact.updated_at) : Number.NaN;
        const shouldUseDraft = !current.playwright_script || Number.isNaN(savedUpdatedAt) || (!Number.isNaN(draftUpdatedAt) && draftUpdatedAt >= savedUpdatedAt);

        scriptsByCaseId[String(caseId)] = {
          ...current,
          playwright_script: shouldUseDraft ? draftScript : current.playwright_script,
          assumptions: shouldUseDraft
            ? (Array.isArray(artifact?.assumptions) ? artifact.assumptions : [])
            : current.assumptions,
          warnings: shouldUseDraft
            ? (Array.isArray(artifact?.warnings) ? artifact.warnings : [])
            : current.warnings,
          model: shouldUseDraft ? (artifact?.model || null) : current.model,
          status: shouldUseDraft ? (artifact?.status || current.status || null) : current.status,
          draft_updated_at: artifact?.updated_at || null,
          requires_regeneration: shouldUseDraft
            ? Boolean(artifact?.requires_regeneration || current.requires_regeneration)
            : current.requires_regeneration,
          stale_reason: shouldUseDraft ? (artifact?.stale_reason || current.stale_reason || null) : current.stale_reason,
          missing_poms: shouldUseDraft
            ? (Array.isArray(artifact?.missing_poms) ? artifact.missing_poms : current.missing_poms || [])
            : current.missing_poms,
          edited_by: shouldUseDraft ? (artifact?.edited_by || current.edited_by || null) : current.edited_by,
          edited_by_name: shouldUseDraft ? (artifact?.edited_by_name || current.edited_by_name || null) : current.edited_by_name,
          edited_by_email: shouldUseDraft ? (artifact?.edited_by_email || current.edited_by_email || null) : current.edited_by_email,
        };
      });
    }

    if (Object.keys(scriptsByCaseId).length > 0) {
      return scriptsByCaseId;
    }

    const activeScriptPayload = scriptsData?.active?.payload || {};
    const activeScriptRequest = scriptsData?.active?.request_payload || {};
    const fallbackCaseId =
      activeScriptRequest?.test_case_id ||
      activeScriptRequest?.test_case?.test_case_no ||
      null;
    const fallbackScript = String(activeScriptPayload?.playwright_script || '').trim();

    if (fallbackCaseId && fallbackScript) {
      scriptsByCaseId[String(fallbackCaseId)] = {
        playwright_script: fallbackScript,
        assumptions: Array.isArray(activeScriptPayload?.assumptions) ? activeScriptPayload.assumptions : [],
        warnings: Array.isArray(activeScriptPayload?.warnings) ? activeScriptPayload.warnings : [],
        model: activeScriptPayload?.model || null,
        version: scriptsData?.active?.version ?? null,
        status: scriptsData?.active?.status || null,
        updated_at: scriptsData?.active?.updated_at || null,
        draft_updated_at: null,
        requires_regeneration: Boolean(activeScriptRequest?.requires_regeneration),
        stale_reason: activeScriptRequest?.stale_reason || null,
        missing_poms: Array.isArray(activeScriptRequest?.missing_poms) ? activeScriptRequest.missing_poms : [],
        edited_by:
          activeScriptRequest?.edited_by
          || activeScriptRequest?.generated_by
          || null,
        edited_by_name:
          activeScriptRequest?.edited_by_name
          || activeScriptRequest?.generated_by_name
          || null,
        edited_by_email:
          activeScriptRequest?.edited_by_email
          || activeScriptRequest?.generated_by_email
          || null,
      };
    }

    return scriptsByCaseId;
  };

  const buildHistoryProcessingResult = ({
    latestArtifacts,
    normalizedTestCases,
    selectedPdfId,
    selectedSectionNumber,
  }) => {
    const toCount = (value, fallback = 0) => {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
      return fallback;
    };
    const historyImageCount = toCount(
      latestArtifacts?.image_count,
      toCount(
        latestArtifacts?.pdf_images_count,
        toCount(latestArtifacts?.total_context_images, 0)
      )
    );
    const historyPdfImagesCount = toCount(
      latestArtifacts?.pdf_images_count,
      historyImageCount
    );
    const historyTotalContextImages = toCount(
      latestArtifacts?.total_context_images,
      historyPdfImagesCount
    );
    const historyTextChunksCount = toCount(latestArtifacts?.text_chunks_count, 0);

    const activeTestArtifact = latestArtifacts?.test_cases?.active || null;
    const draftTestArtifact = latestArtifacts?.test_cases?.draft || null;
    const resolvedTestArtifact = draftTestArtifact || activeTestArtifact;
    const generatedAt = resolvedTestArtifact?.created_at || resolvedTestArtifact?.updated_at || null;
    const generatedByName =
      resolvedTestArtifact?.request_payload?.generated_by_name ||
      resolvedTestArtifact?.payload?.generated_by_name ||
      resolvedTestArtifact?.generated_by_name ||
      null;
    const generatedByEmail =
      resolvedTestArtifact?.request_payload?.generated_by_email ||
      resolvedTestArtifact?.payload?.generated_by_email ||
      resolvedTestArtifact?.generated_by_email ||
      resolvedTestArtifact?.owner_user_email ||
      resolvedTestArtifact?.owner_email ||
      null;
    const generatedBy =
      generatedByName ||
      generatedByEmail ||
      resolvedTestArtifact?.request_payload?.generated_by ||
      resolvedTestArtifact?.payload?.generated_by ||
      resolvedTestArtifact?.generated_by ||
      resolvedTestArtifact?.owner_user_id ||
      null;
    const editedByName =
      resolvedTestArtifact?.request_payload?.edited_by_name ||
      resolvedTestArtifact?.payload?.edited_by_name ||
      resolvedTestArtifact?.edited_by_name ||
      null;
    const editedByEmail =
      resolvedTestArtifact?.request_payload?.edited_by_email ||
      resolvedTestArtifact?.payload?.edited_by_email ||
      resolvedTestArtifact?.edited_by_email ||
      null;
    const editedBy =
      editedByName ||
      editedByEmail ||
      resolvedTestArtifact?.request_payload?.edited_by ||
      resolvedTestArtifact?.payload?.edited_by ||
      resolvedTestArtifact?.edited_by ||
      null;
    const editedAt = editedBy
      ? (
          resolvedTestArtifact?.request_payload?.edited_at
          || resolvedTestArtifact?.updated_at
          || null
        )
      : null;
    const manualImportMeta =
      resolvedTestArtifact?.request_payload?.manual_import
      || activeTestArtifact?.request_payload?.manual_import
      || null;
    const enrichmentMetrics =
      resolvedTestArtifact?.metrics?.enrichment && typeof resolvedTestArtifact.metrics.enrichment === 'object'
        ? resolvedTestArtifact.metrics.enrichment
        : {};
    const payloadMeta =
      resolvedTestArtifact?.payload && typeof resolvedTestArtifact.payload === 'object'
        ? resolvedTestArtifact.payload
        : {};
    const enrichmentStatus =
      payloadMeta.enrichment_status
      || enrichmentMetrics.status
      || null;
    const inputSetsStatus =
      payloadMeta.input_sets_status
      || enrichmentMetrics.input_sets_status
      || null;
    const gapAnalysisStatus =
      payloadMeta.gap_analysis_status
      || enrichmentMetrics.gap_analysis_status
      || null;
    const enrichedAt =
      payloadMeta.enriched_at
      || enrichmentMetrics.enriched_at
      || null;
    const coverageGaps = Array.isArray(payloadMeta.coverage_gaps)
      ? payloadMeta.coverage_gaps
      : (Array.isArray(enrichmentMetrics.coverage_gaps) ? enrichmentMetrics.coverage_gaps : []);
    const suggestedAdditional =
      payloadMeta.suggested_additional
      || enrichmentMetrics.suggested_additional
      || null;

    if (Array.isArray(normalizedTestCases)) {
      return {
        pdf_id: selectedPdfId,
        section_title: latestArtifacts?.section_title || `Section ${selectedSectionNumber}`,
        section_number: selectedSectionNumber,
        status: 'completed',
        generated_at: generatedAt,
        generated_by: generatedBy,
        generated_by_name: generatedByName,
        generated_by_email: generatedByEmail,
        edited_at: editedAt,
        edited_by: editedBy,
        edited_by_name: editedByName,
        edited_by_email: editedByEmail,
        loaded_from_history: true,
        text_chunks_count: historyTextChunksCount,
        image_count: historyImageCount,
        pdf_images_count: historyPdfImagesCount,
        total_context_images: historyTotalContextImages,
        test_cases: normalizedTestCases,
        manual_import: manualImportMeta,
        enrichment_status: enrichmentStatus,
        input_sets_status: inputSetsStatus,
        gap_analysis_status: gapAnalysisStatus,
        enriched_at: enrichedAt,
        coverage_gaps: coverageGaps,
        suggested_additional: suggestedAdditional,
      };
    }

    const positive = Array.isArray(normalizedTestCases?.positive)
      ? normalizedTestCases.positive
      : [];
    const negative = Array.isArray(normalizedTestCases?.negative)
      ? normalizedTestCases.negative
      : [];
    return {
      pdf_id: selectedPdfId,
      section_title: latestArtifacts?.section_title || `Section ${selectedSectionNumber}`,
      section_number: selectedSectionNumber,
      status: 'completed',
      generated_at: generatedAt,
      generated_by: generatedBy,
      generated_by_name: generatedByName,
      generated_by_email: generatedByEmail,
      edited_at: editedAt,
      edited_by: editedBy,
      edited_by_name: editedByName,
      edited_by_email: editedByEmail,
      loaded_from_history: true,
      text_chunks_count: historyTextChunksCount,
      image_count: historyImageCount,
      pdf_images_count: historyPdfImagesCount,
      total_context_images: historyTotalContextImages,
      test_cases: {
        positive,
        negative,
        positive_count: Number(normalizedTestCases?.positive_count ?? positive.length),
        negative_count: Number(normalizedTestCases?.negative_count ?? negative.length),
        total_count: Number(normalizedTestCases?.total_count ?? (positive.length + negative.length)),
      },
      manual_import: manualImportMeta,
      enrichment_status: enrichmentStatus,
      input_sets_status: inputSetsStatus,
      gap_analysis_status: gapAnalysisStatus,
      enriched_at: enrichedAt,
      coverage_gaps: coverageGaps,
      suggested_additional: suggestedAdditional,
    };
  };

  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === selectedWorkspaceId) || null,
    [workspaces, selectedWorkspaceId]
  );

  const selectedWorkspacePermissions = useMemo(
    () => new Set(
      (Array.isArray(selectedWorkspace?.current_user_permissions)
        ? selectedWorkspace.current_user_permissions
        : []
      )
        .map((permission) => String(permission || '').trim().toLowerCase())
        .filter(Boolean)
    ),
    [selectedWorkspace?.current_user_permissions]
  );
  const currentUserWorkspacePermissions = useMemo(
    () => new Set(
      (Array.isArray(currentUser?.application_permissions)
        ? currentUser.application_permissions
        : []
      )
        .map((permission) => String(permission || '').trim().toLowerCase())
        .filter(Boolean)
    ),
    [currentUser?.application_permissions]
  );
  const hasSelectedWorkspacePermission = useCallback(
    (permission) => selectedWorkspacePermissions.has(String(permission || '').trim().toLowerCase()),
    [selectedWorkspacePermissions]
  );
  const hasCurrentUserWorkspacePermission = useCallback(
    (permission) => currentUserWorkspacePermissions.has(String(permission || '').trim().toLowerCase()),
    [currentUserWorkspacePermissions]
  );

  const resolveActiveSectionNumber = useCallback((overrideSectionNumber = null) => {
    const candidates = [
      overrideSectionNumber,
      processingResult?.section_number,
      selectedSection,
    ];
    for (const candidate of candidates) {
      const normalized = Number(candidate);
      if (Number.isFinite(normalized) && normalized > 0) {
        return Math.floor(normalized);
      }
    }
    return null;
  }, [processingResult?.section_number, selectedSection]);

  const currentGlobalRole = String(currentUser?.role || '').trim().toLowerCase();
  const canGloballyManageWorkspaceMembers = currentGlobalRole === 'lead';
  const canCreateWorkspace =
    !applicationLocked && hasCurrentUserWorkspacePermission('application:create');
  const canViewWorkspaceMembers = hasSelectedWorkspacePermission('application:view');
  const canManageWorkspaceMembers =
    canGloballyManageWorkspaceMembers &&
    hasSelectedWorkspacePermission('application:manage_members');
  const canRequestApproval = hasSelectedWorkspacePermission('brd:request_approval');
  const canApproveApproval = hasSelectedWorkspacePermission('brd:approve');
  const canRequestBrdSharing = hasSelectedWorkspacePermission('brd:share_request');
  const canApproveBrdSharing = hasSelectedWorkspacePermission('brd:share_approve');
  const canRequestTestCaseApproval = hasSelectedWorkspacePermission('testcases:request_approval');
  const canApproveTestCaseApproval = hasSelectedWorkspacePermission('testcases:approve');
  const canViewApprovalQueue =
    canApproveApproval ||
    hasCurrentUserWorkspacePermission('brd:approve') ||
    hasCurrentUserWorkspacePermission('brd:share_approve') ||
    hasCurrentUserWorkspacePermission('testcases:approve');
  const activeTestCaseApprovalState = normalizeTestCaseApprovalState(
    testCaseApprovalStatus?.approval_state
  );
  const hasTestCaseApprovalContext = Boolean(resolveActiveSectionNumber());
  const isTestCaseApprovedForCurrentSection = hasTestCaseApprovalContext
    ? (Boolean(testCaseApprovalStatus) && (
      !Boolean(testCaseApprovalStatus?.approval_required)
      || activeTestCaseApprovalState === 'approved'
    ))
    : false;

  const resetProcessingMode = useCallback(() => {
    setProcessingMode('pdf-only');
  }, []);

  const clearReusableSections = useCallback(() => {
    setReusableSections({
      available: false,
      stale: false,
      count: 0,
      extractionMethod: '',
      itemType: '',
    });
  }, []);

  const handlePdfExtractionModeChange = useCallback((mode) => {
    const normalizedMode = mode === 'features'
      ? 'features'
      : 'auto';
    setPdfExtractionMode(normalizedMode);
  }, []);

  const fetchAutomationBaseUrl = useCallback(async (targetPdfId = pdfId) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId) {
      setAutomationBaseUrl('');
      setAutomationBaseUrlError('');
      return '';
    }

    setAutomationBaseUrlLoading(true);
    setAutomationBaseUrlError('');
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.PDF_AUTOMATION_BASE_URL(normalizedPdfId)}`
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load Automation Base URL'));
      }
      const data = await response.json().catch(() => ({}));
      const resolved = String(data?.automation_base_url || '').trim();
      setAutomationBaseUrl(resolved);
      return resolved;
    } catch (err) {
      setAutomationBaseUrl('');
      setAutomationBaseUrlError(err?.message || 'Failed to load Automation Base URL');
      return '';
    } finally {
      setAutomationBaseUrlLoading(false);
    }
  }, [parseApiError, pdfId]);

  const updateAutomationBaseUrl = useCallback(async (nextUrl, targetPdfId = pdfId) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId) {
      throw new Error('Select a BRD before updating Automation Base URL.');
    }
    const normalizedUrl = String(nextUrl || '').trim();

    setAutomationBaseUrlSaving(true);
    setAutomationBaseUrlError('');
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.PDF_AUTOMATION_BASE_URL(normalizedPdfId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ automation_base_url: normalizedUrl || null }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to update Automation Base URL'));
      }
      const data = await response.json().catch(() => ({}));
      const saved = String(data?.automation_base_url || '').trim();
      setAutomationBaseUrl(saved);
      return saved;
    } catch (err) {
      const message = err?.message || 'Failed to update Automation Base URL';
      setAutomationBaseUrlError(message);
      throw new Error(message);
    } finally {
      setAutomationBaseUrlSaving(false);
    }
  }, [parseApiError, pdfId]);

  useEffect(() => {
    if (!pdfId) {
      setAutomationBaseUrl('');
      setAutomationBaseUrlError('');
      return;
    }
    fetchAutomationBaseUrl(pdfId);
  }, [fetchAutomationBaseUrl, pdfId]);

  const loadWorkspaces = useCallback(async () => {
    setWorkspaceLoading(true);
    setWorkspaceError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS}`);
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load applications'));
      }
      const data = await response.json();
      const rows = Array.isArray(data?.applications)
        ? data.applications
        : (Array.isArray(data?.workspaces) ? data.workspaces : []);
      setWorkspaces(rows);
      if (applicationLocked) {
        setSelectedWorkspaceIdState(lockedApplicationId);
        return rows;
      }
      const selectedStillExists = rows.some((row) => row?.id === selectedWorkspaceId);
      if ((!selectedWorkspaceId || !selectedStillExists) && rows.length > 0) {
        if (rows[0]?.id) setSelectedWorkspaceIdState(rows[0].id);
      }
      return rows;
    } catch (err) {
      setWorkspaceError(err?.message || 'Failed to load applications');
      return [];
    } finally {
      setWorkspaceLoading(false);
    }
  }, [
    applicationLocked,
    lockedApplicationId,
    parseApiError,
    selectedWorkspaceId,
  ]);

  const createWorkspace = async ({ name, source_type = 'application' }) => {
    if (applicationLocked) {
      throw new Error('Create applications from the Applications dashboard.');
    }
    const normalizedName = String(name || '').trim();
    if (!normalizedName) {
      throw new Error('Application name is required');
    }

    setWorkspaceError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: normalizedName, source_type }),
      });
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to create application'));
      }
      const data = await response.json();
      const workspace = data?.application || data?.workspace || null;
      const latest = await loadWorkspaces();
      const nextWorkspaceId = workspace?.id || latest?.[0]?.id || '';
      if (nextWorkspaceId) {
        setSelectedWorkspaceId(nextWorkspaceId);
      }
      return workspace;
    } catch (err) {
      setWorkspaceError(err?.message || 'Failed to create application');
      throw err;
    }
  };

  const loadWorkspaceMembers = useCallback(async (workspaceId = selectedWorkspaceId) => {
    const normalizedWorkspaceId = String(workspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      setWorkspaceMembers([]);
      return [];
    }

    setWorkspaceMembersLoading(true);
    setWorkspaceMembersError('');
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_MEMBERS(normalizedWorkspaceId)}`
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load application members'));
      }
      const data = await response.json();
      const members = Array.isArray(data?.members) ? data.members : [];
      setWorkspaceMembers(members);
      return members;
    } catch (err) {
      setWorkspaceMembersError(err?.message || 'Failed to load application members');
      return [];
    } finally {
      setWorkspaceMembersLoading(false);
    }
  }, [parseApiError, selectedWorkspaceId]);

  const addWorkspaceMember = async ({ email, user_id = null, role = null }) => {
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      throw new Error('Select an application first');
    }

    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_MEMBERS(normalizedWorkspaceId)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(email ? { email } : {}),
          ...(user_id ? { user_id } : {}),
          ...(role ? { role } : {}),
        }),
      }
    );
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to add application member'));
    }
    await loadWorkspaceMembers(normalizedWorkspaceId);
  };

  const loadActiveUsers = useCallback(async () => {
    setActiveUsersLoading(true);
    setActiveUsersError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.USERS}`);
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load active users'));
      }
      const data = await response.json();
      const users = Array.isArray(data?.users) ? data.users : [];
      setActiveUsers(users);
      return users;
    } catch (err) {
      setActiveUsers([]);
      setActiveUsersError(err?.message || 'Failed to load active users');
      return [];
    } finally {
      setActiveUsersLoading(false);
    }
  }, [parseApiError]);

  const updateWorkspaceMemberRole = async (memberUserId, role) => {
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    const normalizedMemberId = String(memberUserId || '').trim();
    if (!normalizedWorkspaceId || !normalizedMemberId) {
      return;
    }
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_MEMBER_BY_ID(normalizedWorkspaceId, normalizedMemberId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      }
    );
    if (!response.ok) {
      setWorkspaceMembersError(await parseApiError(response, 'Failed to update member role'));
      return;
    }
    await loadWorkspaceMembers(normalizedWorkspaceId);
    await loadWorkspaces();
  };

  const removeWorkspaceMember = async (memberUserId, displayLabel = '') => {
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    const normalizedMemberId = String(memberUserId || '').trim();
    if (!normalizedWorkspaceId || !normalizedMemberId) {
      return;
    }
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_MEMBER_BY_ID(normalizedWorkspaceId, normalizedMemberId)}`,
      {
        method: 'DELETE',
      }
    );
    if (!response.ok) {
      setWorkspaceMembersError(await parseApiError(response, 'Failed to remove application member'));
      return;
    }
    await loadWorkspaceMembers(normalizedWorkspaceId);
    await loadWorkspaces();
  };

  const fetchApprovalStatus = useCallback(async (targetPdfId = pdfId) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId) {
      setApprovalStatus(null);
      return null;
    }
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.PDF_APPROVAL_STATUS(normalizedPdfId)}`
    );
    if (!response.ok) {
      return null;
    }
    const data = await response.json().catch(() => ({}));
    const approval = data?.approval || null;
    if (!approval) return null;
    const normalizedApproval = {
      ...approval,
      approval_state: normalizeApprovalState(approval.approval_state),
    };
    setApprovalStatus(normalizedApproval);
    return normalizedApproval;
  }, [normalizeApprovalState, pdfId]);

  const fetchApprovalEvents = useCallback(async (targetPdfId = pdfId) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId) {
      setApprovalEvents([]);
      return [];
    }
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.PDF_APPROVAL_EVENTS(normalizedPdfId)}`
    );
    if (!response.ok) return [];
    const data = await response.json().catch(() => ({}));
    const rows = Array.isArray(data?.events) ? data.events : [];
    setApprovalEvents(rows);
    return rows;
  }, [pdfId]);

  const fetchBrdSharingStatus = useCallback(async (targetPdfId = pdfId) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId) {
      setBrdSharingStatus(null);
      return null;
    }
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.BRD_SHARING_STATUS(normalizedPdfId)}`
    );
    if (!response.ok) {
      setBrdSharingStatus(null);
      return null;
    }
    const data = await response.json().catch(() => ({}));
    const sharing = data?.sharing || null;
    if (!sharing) {
      setBrdSharingStatus(null);
      return null;
    }
    setBrdSharingStatus(sharing);
    return sharing;
  }, [pdfId]);

  const fetchBrdSharingRequests = useCallback(async (targetPdfId = pdfId, status = 'pending') => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId) {
      setBrdSharingRequests([]);
      return [];
    }
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.BRD_SHARING_REQUESTS(normalizedPdfId)}${query}`
    );
    if (!response.ok) {
      setBrdSharingRequests([]);
      return [];
    }
    const data = await response.json().catch(() => ({}));
    const rows = Array.isArray(data?.requests) ? data.requests : [];
    setBrdSharingRequests(rows);
    return rows;
  }, [pdfId]);

  const loadBrdSharingForPdf = useCallback(async (targetPdfId = pdfId) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId) {
      setBrdSharingStatus(null);
      setBrdSharingRequests([]);
      return { sharing: null, requests: [] };
    }

    setBrdSharingLoading(true);
    try {
      const [sharing, requests] = await Promise.all([
        fetchBrdSharingStatus(normalizedPdfId),
        fetchBrdSharingRequests(normalizedPdfId, 'pending'),
      ]);
      return { sharing, requests };
    } finally {
      setBrdSharingLoading(false);
    }
  }, [fetchBrdSharingRequests, fetchBrdSharingStatus, pdfId]);

  const fetchTestCaseApprovalStatus = useCallback(async (
    targetPdfId = pdfId,
    targetSectionNumber = null,
  ) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    const resolvedSectionNumber = resolveActiveSectionNumber(targetSectionNumber);
    if (!normalizedPdfId || !resolvedSectionNumber) {
      setTestCaseApprovalStatus(null);
      return null;
    }

    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_APPROVAL_STATUS(normalizedPdfId, resolvedSectionNumber)}`
    );
    if (!response.ok) {
      setTestCaseApprovalStatus(null);
      return null;
    }

    const data = await response.json().catch(() => ({}));
    const approval = data?.approval || null;
    if (!approval) return null;
    const normalizedApproval = {
      ...approval,
      approval_state: normalizeTestCaseApprovalState(approval.approval_state),
      section_number: Number(approval.section_number || resolvedSectionNumber),
    };
    setTestCaseApprovalStatus(normalizedApproval);
    return normalizedApproval;
  }, [normalizeTestCaseApprovalState, pdfId, resolveActiveSectionNumber]);

  const fetchTestCaseApprovalEvents = useCallback(async (
    targetPdfId = pdfId,
    targetSectionNumber = null,
  ) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    const resolvedSectionNumber = resolveActiveSectionNumber(targetSectionNumber);
    if (!normalizedPdfId || !resolvedSectionNumber) {
      setTestCaseApprovalEvents([]);
      return [];
    }

    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_APPROVAL_EVENTS(normalizedPdfId, resolvedSectionNumber)}`
    );
    if (!response.ok) {
      setTestCaseApprovalEvents([]);
      return [];
    }
    const data = await response.json().catch(() => ({}));
    const rows = Array.isArray(data?.events) ? data.events : [];
    setTestCaseApprovalEvents(rows);
    return rows;
  }, [pdfId, resolveActiveSectionNumber]);

  const requestBrdApproval = async () => {
    if (!pdfId) return;
    setApprovalLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.PDF_APPROVAL_REQUEST(pdfId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to request approval'));
      }
      const data = await response.json();
      const nextApproval = data?.approval || null;
      if (nextApproval) {
        setApprovalStatus({
          ...nextApproval,
          approval_state: normalizeApprovalState(nextApproval.approval_state),
        });
      }
      await fetchApprovalEvents(pdfId);
      if (canViewApprovalQueue) {
        await loadPendingApprovals();
      }
    } catch (err) {
      setError(err?.message || 'Failed to request approval');
      throw err;
    } finally {
      setApprovalLoading(false);
    }
  };

  const requestTestCaseApproval = async () => {
    if (!pdfId) return;
    const sectionNumber = resolveActiveSectionNumber();
    if (!sectionNumber) {
      setError('Select a section with saved test cases before requesting approval.');
      return;
    }
    setTestCaseApprovalLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_APPROVAL_REQUEST(pdfId, sectionNumber)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to request test-case approval'));
      }
      const data = await response.json();
      const nextApproval = data?.approval || null;
      if (nextApproval) {
        setTestCaseApprovalStatus({
          ...nextApproval,
          approval_state: normalizeTestCaseApprovalState(nextApproval.approval_state),
          section_number: Number(nextApproval.section_number || sectionNumber),
        });
      }
      await fetchTestCaseApprovalEvents(pdfId, sectionNumber);
      return data;
    } catch (err) {
      setError(err?.message || 'Failed to request test-case approval');
      throw err;
    } finally {
      setTestCaseApprovalLoading(false);
    }
  };

  const remindTestCaseApproval = async () => {
    if (!pdfId) return;
    const sectionNumber = resolveActiveSectionNumber();
    if (!sectionNumber) {
      setError('Select a section with saved test cases before sending reminder.');
      return;
    }
    setTestCaseApprovalLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_APPROVAL_REQUEST(pdfId, sectionNumber)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'Reminder: please review test cases for approval' }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to send test-case approval reminder'));
      }
      const data = await response.json();
      const nextApproval = data?.approval || null;
      if (nextApproval) {
        setTestCaseApprovalStatus({
          ...nextApproval,
          approval_state: normalizeTestCaseApprovalState(nextApproval.approval_state),
          section_number: Number(nextApproval.section_number || sectionNumber),
        });
      }
      await fetchTestCaseApprovalEvents(pdfId, sectionNumber);
      return data;
    } catch (err) {
      setError(err?.message || 'Failed to send test-case approval reminder');
      throw err;
    } finally {
      setTestCaseApprovalLoading(false);
    }
  };

  const approveTestCaseApproval = async () => {
    if (!pdfId) return;
    const sectionNumber = resolveActiveSectionNumber();
    if (!sectionNumber) {
      setError('Select a section before approving test cases.');
      return;
    }
    setTestCaseApprovalLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_APPROVAL_APPROVE(pdfId, sectionNumber)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to approve test cases'));
      }
      const data = await response.json();
      const nextApproval = data?.approval || null;
      if (nextApproval) {
        setTestCaseApprovalStatus({
          ...nextApproval,
          approval_state: normalizeTestCaseApprovalState(nextApproval.approval_state),
          section_number: Number(nextApproval.section_number || sectionNumber),
        });
      }
      await fetchTestCaseApprovalEvents(pdfId, sectionNumber);
      return data;
    } catch (err) {
      setError(err?.message || 'Failed to approve test cases');
      throw err;
    } finally {
      setTestCaseApprovalLoading(false);
    }
  };

  const rejectTestCaseApproval = async () => {
    if (!pdfId) return;
    const sectionNumber = resolveActiveSectionNumber();
    if (!sectionNumber) {
      setError('Select a section before rejecting test cases.');
      return;
    }
    const reason = window.prompt('Reason for changes requested');
    if (!reason || !String(reason).trim()) {
      return;
    }
    setTestCaseApprovalLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_APPROVAL_REJECT(pdfId, sectionNumber)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: String(reason).trim() }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to request testcase changes'));
      }
      const data = await response.json();
      const nextApproval = data?.approval || null;
      if (nextApproval) {
        setTestCaseApprovalStatus({
          ...nextApproval,
          approval_state: normalizeTestCaseApprovalState(nextApproval.approval_state),
          section_number: Number(nextApproval.section_number || sectionNumber),
        });
      }
      await fetchTestCaseApprovalEvents(pdfId, sectionNumber);
      return data;
    } catch (err) {
      setError(err?.message || 'Failed to request testcase changes');
      throw err;
    } finally {
      setTestCaseApprovalLoading(false);
    }
  };

  const remindBrdApproval = async () => {
    if (!pdfId) return;
    setApprovalLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.PDF_APPROVAL_REQUEST(pdfId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'Reminder: approval follow-up requested' }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to send approval reminder'));
      }
      const data = await response.json();
      const nextApproval = data?.approval || null;
      if (nextApproval) {
        setApprovalStatus({
          ...nextApproval,
          approval_state: normalizeApprovalState(nextApproval.approval_state),
        });
      }
      await fetchApprovalEvents(pdfId);
      if (canViewApprovalQueue) {
        await loadPendingApprovals();
      }
    } catch (err) {
      setError(err?.message || 'Failed to send approval reminder');
      throw err;
    } finally {
      setApprovalLoading(false);
    }
  };

  const approveBrd = async () => {
    if (!pdfId) return;
    setApprovalLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.PDF_APPROVAL_APPROVE(pdfId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to approve BRD'));
      }
      const data = await response.json();
      const nextApproval = data?.approval || null;
      if (nextApproval) {
        setApprovalStatus({
          ...nextApproval,
          approval_state: normalizeApprovalState(nextApproval.approval_state),
        });
      }
      await fetchApprovalEvents(pdfId);
      await loadPendingApprovals();
    } catch (err) {
      setError(err?.message || 'Failed to approve BRD');
      throw err;
    } finally {
      setApprovalLoading(false);
    }
  };

  const rejectBrd = async () => {
    if (!pdfId) return;
    const reason = window.prompt('Rejection reason');
    if (!reason || !String(reason).trim()) {
      return;
    }
    setApprovalLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.PDF_APPROVAL_REJECT(pdfId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: String(reason).trim() }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to reject BRD'));
      }
      const data = await response.json();
      const nextApproval = data?.approval || null;
      if (nextApproval) {
        setApprovalStatus({
          ...nextApproval,
          approval_state: normalizeApprovalState(nextApproval.approval_state),
        });
      }
      await fetchApprovalEvents(pdfId);
      await loadPendingApprovals();
    } catch (err) {
      setError(err?.message || 'Failed to reject BRD');
      throw err;
    } finally {
      setApprovalLoading(false);
    }
  };

  const requestBrdShare = async ({ targetUserId, reason = null, pdfId: requestPdfId = null } = {}) => {
    const targetPdfId = String(requestPdfId || pdfId || '').trim();
    if (!targetPdfId) return;
    const normalizedTargetUserId = String(targetUserId || '').trim();
    if (!normalizedTargetUserId) {
      setError('Select an application user before requesting BRD sharing.');
      return;
    }
    setBrdSharingLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.BRD_SHARING_REQUEST(targetPdfId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            target_user_id: normalizedTargetUserId,
            ...(reason ? { reason } : {}),
          }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to request BRD sharing'));
      }
      const data = await response.json().catch(() => ({}));
      if (data?.sharing) {
        setBrdSharingStatus(data.sharing);
      }
      await fetchBrdSharingRequests(targetPdfId, 'pending');
      return data;
    } catch (err) {
      setError(err?.message || 'Failed to request BRD sharing');
      throw err;
    } finally {
      setBrdSharingLoading(false);
    }
  };

  const approveBrdShareRequest = async (requestId, requestPdfId = null) => {
    const targetPdfId = String(requestPdfId || pdfId || '').trim();
    if (!targetPdfId) return;
    const normalizedRequestId = String(requestId || '').trim();
    if (!normalizedRequestId) return;
    setBrdSharingLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.BRD_SHARING_APPROVE(targetPdfId, normalizedRequestId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to approve BRD sharing'));
      }
      const data = await response.json().catch(() => ({}));
      if (data?.sharing) {
        setBrdSharingStatus(data.sharing);
      }
      await fetchBrdSharingRequests(targetPdfId, 'pending');
      return data;
    } catch (err) {
      setError(err?.message || 'Failed to approve BRD sharing');
      throw err;
    } finally {
      setBrdSharingLoading(false);
    }
  };

  const rejectBrdShareRequest = async (requestId, requestPdfId = null) => {
    const targetPdfId = String(requestPdfId || pdfId || '').trim();
    if (!targetPdfId) return;
    const normalizedRequestId = String(requestId || '').trim();
    if (!normalizedRequestId) return;
    const reason = window.prompt('Reason for rejecting this share request (optional)') || '';
    setBrdSharingLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.BRD_SHARING_REJECT(targetPdfId, normalizedRequestId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(String(reason).trim() ? { reason: String(reason).trim() } : {}),
          }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to reject BRD sharing'));
      }
      const data = await response.json().catch(() => ({}));
      if (data?.sharing) {
        setBrdSharingStatus(data.sharing);
      }
      await fetchBrdSharingRequests(targetPdfId, 'pending');
      return data;
    } catch (err) {
      setError(err?.message || 'Failed to reject BRD sharing');
      throw err;
    } finally {
      setBrdSharingLoading(false);
    }
  };

  const revokeBrdShareAccess = async (targetUserId, requestPdfId = null) => {
    const targetPdfId = String(requestPdfId || pdfId || '').trim();
    const normalizedTargetUserId = String(targetUserId || '').trim();
    if (!targetPdfId || !normalizedTargetUserId) return;
    setBrdSharingLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.BRD_SHARING_REVOKE(
          targetPdfId,
          encodeURIComponent(normalizedTargetUserId)
        )}`,
        {
          method: 'DELETE',
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to remove BRD access'));
      }
      const data = await response.json().catch(() => ({}));
      if (data?.sharing) {
        setBrdSharingStatus(data.sharing);
      }
      await fetchBrdSharingRequests(targetPdfId, 'pending');
      return data;
    } catch (err) {
      setError(err?.message || 'Failed to remove BRD access');
      throw err;
    } finally {
      setBrdSharingLoading(false);
    }
  };

  const loadPendingApprovals = useCallback(async () => {
    if (!canViewApprovalQueue) {
      setPendingApprovals([]);
      return [];
    }
    setPendingApprovalsLoading(true);
    try {
      const workspaceQuery = selectedWorkspaceId
        ? `?application_id=${encodeURIComponent(selectedWorkspaceId)}`
        : '';
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.PDF_APPROVAL_PENDING}${workspaceQuery}`
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load approvals'));
      }
      const data = await response.json();
      const rows = Array.isArray(data?.items) ? data.items : [];
      setPendingApprovals(rows);
      return rows;
    } catch (_err) {
      setPendingApprovals([]);
      return [];
    } finally {
      setPendingApprovalsLoading(false);
    }
  }, [canViewApprovalQueue, parseApiError, selectedWorkspaceId]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  useEffect(() => {
    if (!showWorkspaceMembersModal) return;
    loadWorkspaceMembers();
    if (canManageWorkspaceMembers) {
      loadActiveUsers();
    }
  }, [
    canManageWorkspaceMembers,
    loadActiveUsers,
    loadWorkspaceMembers,
    showWorkspaceMembersModal,
    selectedWorkspaceId,
  ]);

  useEffect(() => {
    if (currentStep < 3) return;
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    if (!normalizedWorkspaceId) return;
    loadWorkspaceMembers(normalizedWorkspaceId);
  }, [currentStep, loadWorkspaceMembers, selectedWorkspaceId]);

  useEffect(() => {
    if (!pdfId) {
      setApprovalStatus(null);
      setApprovalEvents([]);
      setBrdSharingStatus(null);
      setBrdSharingRequests([]);
      return;
    }
    fetchApprovalStatus(pdfId);
    fetchApprovalEvents(pdfId);
    loadBrdSharingForPdf(pdfId);
  }, [fetchApprovalEvents, fetchApprovalStatus, loadBrdSharingForPdf, pdfId]);

  useEffect(() => {
    const sectionNumber = resolveActiveSectionNumber();
    if (!pdfId || !sectionNumber) {
      setTestCaseApprovalStatus(null);
      setTestCaseApprovalEvents([]);
      return;
    }
    fetchTestCaseApprovalStatus(pdfId, sectionNumber);
    fetchTestCaseApprovalEvents(pdfId, sectionNumber);
  }, [
    fetchTestCaseApprovalEvents,
    fetchTestCaseApprovalStatus,
    pdfId,
    resolveActiveSectionNumber,
    processingResult?.section_number,
    selectedSection,
  ]);

  useEffect(() => {
    if (canViewApprovalQueue) {
      loadPendingApprovals();
    } else {
      setPendingApprovals([]);
    }
  }, [canViewApprovalQueue, loadPendingApprovals]);

  const handleFileChange = (fileOrEvent) => {
    const selectedFile =
      fileOrEvent instanceof File
        ? fileOrEvent
        : fileOrEvent?.target?.files?.[0];
    if (isSupportedBrdFile(selectedFile)) {
      setFile(selectedFile);
      setOpenApiFile(null);
      setExcelFile(null);
      setExcelImportAnalysis(null);
      setDocumentName(resolveDocumentName(selectedFile));
      setError(null);
      clearReusableSections();
    } else {
      setError('Please select a valid PDF, DOC, or DOCX file');
      setFile(null);
      setDocumentName('');
      clearReusableSections();
    }
  };

  const handleOpenApiFileChange = (fileOrEvent) => {
    const selectedFile =
      fileOrEvent instanceof File
        ? fileOrEvent
        : fileOrEvent?.target?.files?.[0];
    if (isSupportedOpenApiFile(selectedFile)) {
      setOpenApiFile(selectedFile);
      setFile(null);
      setExcelFile(null);
      setExcelImportAnalysis(null);
      setDocumentName(resolveDocumentName(selectedFile) || selectedFile.name || '');
      setError(null);
      clearReusableSections();
    } else {
      setError('Please select a valid OpenAPI or Swagger file (.json, .yaml, .yml)');
      setOpenApiFile(null);
      clearReusableSections();
    }
  };

  const handleExcelFileChange = (fileOrEvent) => {
    const selectedFile =
      fileOrEvent instanceof File
        ? fileOrEvent
        : fileOrEvent?.target?.files?.[0];
    if (isSupportedExcelFile(selectedFile) && Number(selectedFile?.size || 0) <= MAX_EXCEL_IMPORT_BYTES) {
      setExcelFile(selectedFile);
      setExcelImportAnalysis(null);
      setFile(null);
      setOpenApiFile(null);
      setDocumentName(resolveDocumentName(selectedFile) || selectedFile.name || '');
      setError(null);
      clearReusableSections();
      return;
    }
    setExcelFile(null);
    setExcelImportAnalysis(null);
    setError(
      Number(selectedFile?.size || 0) > MAX_EXCEL_IMPORT_BYTES
        ? 'Excel testcase workbooks must be 10 MB or smaller'
        : 'Please select a valid .xlsx testcase workbook'
    );
  };

  const analyzeExcelTestCases = async () => {
    if (!excelFile) {
      setError('Please select an Excel testcase workbook');
      return null;
    }
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      setError('Please select an application before importing test cases.');
      return null;
    }

    setActiveUploadSource('testcase-excel');
    setStatus('uploading');
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('application_id', normalizedWorkspaceId);
      const endpoint = `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_IMPORT_ANALYZE}`;
      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to analyze Excel test cases'));
      }
      let data = await response.json();
      if (data?.job?.id) {
        const queuedJob = announceJob(data.job);
        setStatus('extracting');
        await waitForJobResult(queuedJob);
        const analysisResponse = await apiFetch(
          `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_IMPORT_ANALYSIS(data.import_id)}`
        );
        if (!analysisResponse.ok) {
          throw new Error(await parseApiError(analysisResponse, 'Failed to restore Excel workbook analysis'));
        }
        data = await analysisResponse.json();
      }
      if (!String(data?.import_id || '').trim()) {
        throw new Error('The server did not create an Excel import session.');
      }
      setExcelImportAnalysis(data);
      getSessionStorage()?.setItem(
        excelImportSessionKey,
        JSON.stringify({
          import_id: String(data.import_id || '').trim(),
          application_id: String(data?.application_id || normalizedWorkspaceId || '').trim(),
        })
      );
      setDocumentName(resolveDocumentName(data, excelFile) || excelFile.name || '');
      setStatus('excel_mapping');
      setCurrentStep(1.5);
      return data;
    } catch (err) {
      const message = err?.message || 'Failed to analyze Excel test cases';
      setError(message);
      setStatus('idle');
      return null;
    } finally {
      setActiveUploadSource(null);
    }
  };

  const previewExcelTestCases = async (request = {}) => {
    const importId = String(excelImportAnalysis?.import_id || '').trim();
    if (!importId) {
      throw new Error('Excel import session is missing. Analyze the workbook again.');
    }
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_IMPORT_PREVIEW(importId)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...request,
          application_id: request?.application_id || selectedWorkspaceId || undefined,
        }),
      }
    );
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to preview Excel test cases'));
    }
    return response.json();
  };

  const restoreExcelTestCaseImport = async (importId, options = {}) => {
    const normalizedImportId = String(importId || '').trim();
    if (!normalizedImportId) {
      throw new Error('Excel import session is missing. Analyze the workbook again.');
    }
    const storedPayload = (() => {
      try {
        const raw = getSessionStorage()?.getItem(excelImportSessionKey);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch {
        return {};
      }
    })();
    const requestedWorkspaceId = String(
      options?.application_id
      || storedPayload?.application_id
      || selectedWorkspaceId
      || ''
    ).trim();

    setHistoryRestoreLoading({ type: 'excel-import', importId: normalizedImportId });
    setStatus('uploading');
    setError(null);
    try {
      const body = {
        preview_limit: Number(options?.preview_limit || 100) || 100,
      };
      if (requestedWorkspaceId) {
        body.application_id = requestedWorkspaceId;
      }
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_IMPORT_PREVIEW(normalizedImportId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to restore Excel import session'));
      }
      const data = await response.json();
      const workspaceId = requireMatchingApplication(
        data?.application_id || requestedWorkspaceId
      );
      setExcelImportAnalysis(data);
      setExcelFile(null);
      setFile(null);
      setOpenApiFile(null);
      setPdfId(null);
      setSections([]);
      setRawSectionOutline([]);
      setSectionGrouping(null);
      setSelectedSection(null);
      setProcessingResult(null);
      setEditedTestCases([]);
      setOriginalTestCases([]);
      setHistoryScriptsByCaseId({});
      setRunResults({});
      setDocumentName(resolveDocumentName(data) || 'Imported Test Cases');
      if (workspaceId) {
        setSelectedWorkspaceId(workspaceId);
      }
      getSessionStorage()?.setItem(
        excelImportSessionKey,
        JSON.stringify({ import_id: normalizedImportId, application_id: workspaceId })
      );
      getSessionStorage()?.removeItem(activePdfSessionKey);
      getSessionStorage()?.removeItem('active_pdf_id');
      setActiveUploadSource(null);
      setStatus('excel_mapping');
      setCurrentStep(1.5);
      return data;
    } catch (err) {
      setStatus('idle');
      const message = err?.message || 'Failed to restore Excel import session';
      setError(message);
      throw new Error(message);
    } finally {
      setHistoryRestoreLoading(null);
    }
  };

  const commitExcelTestCases = async (request = {}) => {
    const importId = String(excelImportAnalysis?.import_id || '').trim();
    if (!importId) {
      throw new Error('Excel import session is missing. Analyze the workbook again.');
    }
    const normalizedWorkspaceId = String(
      request?.application_id || selectedWorkspaceId || ''
    ).trim();
    if (!normalizedWorkspaceId) {
      throw new Error('Select an application before creating the testcase source.');
    }

    setStatus('saving');
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_IMPORT_COMMIT(importId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...request,
            application_id: normalizedWorkspaceId,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to create Excel testcase source'));
      }

      const data = await response.json();
      getSessionStorage()?.removeItem(excelImportSessionKey);
      const nextPdfId = String(data?.pdf_id || data?.job_id || '').trim();
      if (!nextPdfId) {
        throw new Error('The server did not return a testcase source id.');
      }
      const sourceTitle = String(
        data?.source?.title
        || data?.source_title
        || request?.source_name
        || excelFile?.name
        || 'Imported Test Cases'
      ).trim();
      const processing = (
        data?.processing_result && typeof data.processing_result === 'object'
          ? data.processing_result
          : {}
      );
      const rawTestCases = (
        processing?.test_cases
        ?? data?.test_cases
        ?? data?.cases
        ?? []
      );
      const normalizeImportedCase = (testCase) => {
        if (!testCase || typeof testCase !== 'object' || Array.isArray(testCase)) {
          return testCase;
        }
        return {
          ...testCase,
          source_type: String(
            testCase.source_type
            || testCase?.source?.source_type
            || testCase?.source?.type
            || 'testcase_excel'
          ).trim().toLowerCase(),
        };
      };
      const testCases = hasCategorizedTestCases(rawTestCases)
        ? {
          ...rawTestCases,
          positive: toCaseArray(rawTestCases.positive).map(normalizeImportedCase),
          negative: toCaseArray(rawTestCases.negative).map(normalizeImportedCase),
        }
        : toCaseArray(rawTestCases).map(normalizeImportedCase);
      const sectionPayload = data?.section && typeof data.section === 'object'
        ? data.section
        : null;
      const sectionNumber = Number(
        processing?.section_number
        || data?.section_number
        || sectionPayload?.number
        || sectionPayload?.section_number
        || 1
      );
      const sectionTitle = String(
        processing?.section_title
        || data?.section_title
        || sectionPayload?.title
        || sourceTitle
      ).trim();
      const fallbackSection = {
        number: sectionNumber,
        section_number: String(sectionNumber),
        title: sectionTitle,
        level: 1,
        indexed_pages: 'Excel testcase import',
        image_count: 0,
        source_type: 'testcase_excel',
      };
      const nextSections = Array.isArray(data?.sections) && data.sections.length > 0
        ? data.sections
        : [sectionPayload || fallbackSection];
      const normalizedProcessingResult = {
        ...processing,
        success: processing?.success !== false,
        status: processing?.status || 'completed',
        source_type: 'testcase_excel',
        item_type: 'testcase_excel',
        pdf_id: nextPdfId,
        section_number: sectionNumber,
        section_title: sectionTitle,
        test_cases: testCases,
        import_manifest: data?.import_manifest || processing?.import_manifest || null,
      };

      clearHistoryLoadedBrd();
      setPdfId(nextPdfId);
      // Keep the BRD upload slot empty. Treating the selected workbook as
      // `file` makes the intake screen offer an .xlsx file to the BRD upload
      // endpoint when the user returns from review.
      setFile(null);
      setOpenApiFile(null);
      setDocumentName(sourceTitle);
      setSelectedPreviousPdf(null);
      setDashboardPdf(null);
      setBrdLoadedFromHistory(false);
      setBrdChangeAnalysis(null);
      setBrdUpdatePending(false);
      setIsReprocessed(false);
      applySectionsResponse({ ...data, sections: nextSections });
      setSelectedSection(sectionNumber);
      setProcessingResult(normalizedProcessingResult);
      setEditedTestCases(testCases);
      setOriginalTestCases(testCases);
      setHistoryScriptsByCaseId({});
      setSectionArtifactIndex({});
      setPreAnalysisPlan(null);
      setApprovedCounts({ positive_count: null, negative_count: null });
      setApprovedFocus([]);
      setApprovedGlobalConstraints([]);
      setApprovedTestCaseFieldConfig(null);
      setDgsConfig(null);
      setTestingType('web');
      setBulkMode('single');
      setBulkSelectedSections([sectionNumber]);
      setBulkJob(null);
      setBulkItems([]);
      setBulkEvents([]);
      setBulkError(null);
      resetProcessingMode();
      setApprovalStatus({
        approval_required: false,
        approval_state: 'approved',
      });
      setApprovalEvents([]);
      setTestCaseApprovalStatus(
        data?.test_case_approval || {
          pdf_id: nextPdfId,
          section_number: sectionNumber,
          approval_state: 'draft',
        }
      );
      setStatus('completed');
      setCurrentStep(5);

      await Promise.allSettled([
        loadPdfArtifactsDashboard(nextPdfId, { updateSectionIndex: true }),
        fetchAutomationBaseUrl(nextPdfId),
        fetchTestCaseApprovalStatus(nextPdfId, sectionNumber),
        fetchTestCaseApprovalEvents(nextPdfId, sectionNumber),
      ]);
      return data;
    } catch (err) {
      const message = err?.message || 'Failed to create Excel testcase source';
      setError(message);
      setStatus('excel_mapping');
      throw new Error(message);
    }
  };

  const cancelExcelTestCaseImport = async () => {
    const importId = String(excelImportAnalysis?.import_id || '').trim();
    if (importId) {
      try {
        await apiFetch(
          `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_IMPORT_CANCEL(importId)}`,
          { method: 'DELETE' }
        );
      } catch {
        // Clearing the local wizard must not trap the user if best-effort
        // temporary-upload cleanup is unavailable.
      }
    }
    setExcelImportAnalysis(null);
    getSessionStorage()?.removeItem(excelImportSessionKey);
    getSessionStorage()?.removeItem(workflowLastRouteSessionKey);
    setExcelFile(null);
    setStatus('idle');
    setError(null);
    setCurrentStep(1);
  };

  const handleManualInputImagesChange = (event) => {
    const selectedFiles = Array.from(event?.target?.files || []);
    const imageValidation = validateManualInputImages(selectedFiles);
    setManualInputImages(imageValidation.files);
    if (imageValidation.message) {
      setError(imageValidation.message);
    } else if (selectedFiles.length || !event?.target?.files?.length) {
      setError(null);
    } else {
      setError('Please choose PNG, JPG, or WebP images.');
    }
  };

  const uploadPDF = async () => {
    if (!file) {
      setError('Please select a BRD document');
      return;
    }
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      setError('Please select an application before uploading.');
      return;
    }

    setActiveUploadSource('document');
    setStatus('uploading');
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadEndpoint = `${API_ENDPOINTS.UPLOAD_PDF}?application_id=${encodeURIComponent(normalizedWorkspaceId)}`;
      const response = await apiFetch(
        `${API_BASE_URL}${uploadEndpoint}`,
        {
        method: 'POST',
        body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Upload failed'));
      }

      const data = await response.json();
      const reusedExistingBrd = Boolean(data?.is_reprocessed && data?.pdf_id);
      const resolvedExistingName = resolveDocumentName(data, file) || file.name || '';
      const existingBrdSelection = reusedExistingBrd
        ? {
          ...data,
          pdf_id: data.pdf_id,
          pdf_file: data?.pdf_file || resolvedExistingName,
          document_name: data?.document_name || resolvedExistingName,
          brd_update_supported: data?.brd_update_supported,
          brd_update_blocked_reason: data?.brd_update_blocked_reason || '',
        }
        : null;
      if (reusedExistingBrd) {
        rememberHistoryLoadedBrd(data.pdf_id);
      } else {
        clearHistoryLoadedBrd();
      }
      setPdfId(data.pdf_id);
      setDocumentName(resolvedExistingName);
      setIsReprocessed(data.is_reprocessed);
      setSelectedPreviousPdf(existingBrdSelection);
      setDashboardPdf(existingBrdSelection);
      setBrdLoadedFromHistory(reusedExistingBrd);
      setBrdChangeAnalysis(data.change_analysis || (data.change_summary ? { summary: data.change_summary } : null));
      setBrdUpdatePending(isPendingBrdUpdatePayload(data));
      setRawSectionOutline([]);
      setSectionGrouping(null);
      setSelectedSection(null);
      setProcessingResult(null);
      setEditedTestCases([]);
      setOriginalTestCases([]);
      setHistoryScriptsByCaseId({});
      setSectionArtifactIndex({});
      setConfirmedReplacementSection(null);
      setPreAnalysisPlan(null);
      setApprovedCounts({ positive_count: null, negative_count: null });
      setApprovedFocus([]);
      setApprovedGlobalConstraints([]);
      setApprovedTestCaseFieldConfig(null);
      setDgsConfig(null);
      setAutomationBaseUrl('');
      setAutomationBaseUrlError('');
      setBulkMode('single');
      setBulkSelectedSections([]);
      setBulkJob(null);
      setBulkItems([]);
      setBulkEvents([]);
      setBulkError(null);
      setQueuedSingleGeneration(null);
      setQueuedPreAnalysis(null);
      clearReusableSections();
      resetProcessingMode();
      setApprovalStatus({
        approval_required: false,
        approval_state: 'approved',
      });
      setApprovalEvents([]);
      await fetchAutomationBaseUrl(data.pdf_id);

      if (data.is_reprocessed) {
        setStatus('extracting');
        try {
          const sectionsResponse = await apiFetch(
            `${API_BASE_URL}${API_ENDPOINTS.SECTIONS}/${data.pdf_id}`
          );
          if (sectionsResponse.ok) {
            const sectionsData = await sectionsResponse.json();
            const restoredSections = applySectionsResponse(sectionsData);
            await loadPdfArtifactsDashboard(data.pdf_id, { updateSectionIndex: true });
            setSelectedPreviousPdf((current) => ({
              ...((current && typeof current === 'object') ? current : existingBrdSelection || {}),
              pdf_id: data.pdf_id,
              pdf_file: data?.pdf_file || resolvedExistingName,
              document_name: data?.document_name || resolvedExistingName,
              brd_version: sectionsData?.brd_version ?? data?.brd_version,
              base_version: sectionsData?.base_version ?? data?.base_version,
              brd_update_supported: sectionsData?.brd_update_supported ?? data?.brd_update_supported,
              brd_update_blocked_reason: sectionsData?.brd_update_blocked_reason || data?.brd_update_blocked_reason || '',
            }));
            setProcessingMode('pdf-only');
            setReusableSections({
              available: restoredSections.length > 0,
              stale: false,
              count: restoredSections.length,
              extractionMethod: sectionsData?.extraction_method || '',
              itemType: sectionsData?.item_type || '',
            });
            setStatus('uploaded');
            setCurrentStep(2);
            await fetchApprovalStatus(data.pdf_id);
            await fetchApprovalEvents(data.pdf_id);
            if (canViewApprovalQueue) {
              await loadPendingApprovals();
            }
            return;
          }
        } catch (sectionLoadError) {
          // If the saved section map can't be loaded, keep the standard extraction step.
        }
      }

      setStatus('uploaded');
      setCurrentStep(2);
      await fetchApprovalStatus(data.pdf_id);
      await fetchApprovalEvents(data.pdf_id);
      if (canViewApprovalQueue) {
        await loadPendingApprovals();
      }
    } catch (err) {
      setError('Failed to upload BRD document: ' + err.message);
      setStatus('idle');
    } finally {
      setActiveUploadSource(null);
    }
  };

  const uploadUpdatedBrdVersion = async (fileOrEvent) => {
    const selectedFile =
      fileOrEvent instanceof File
        ? fileOrEvent
        : fileOrEvent?.target?.files?.[0];
    if (!isSupportedBrdFile(selectedFile)) {
      const message = 'Please select a valid PDF, DOC, or DOCX file';
      setError(message);
      throw new Error(message);
    }

    const baseBrdId = String(pdfId || selectedPreviousPdf?.pdf_id || '').trim();
    if (!brdLoadedFromHistory || !baseBrdId) {
      const message = 'Load an existing BRD from History before uploading an updated version.';
      setError(message);
      throw new Error(message);
    }

    setActiveUploadSource('document');
    setStatus('uploading');
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.BRD_VERSION_UPLOAD(baseBrdId)}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Updated BRD upload is temporarily unavailable. Please try again after refreshing the page.');
        }
        throw new Error(await parseApiError(response, 'Failed to upload updated BRD'));
      }

      const data = await response.json();
      const nextPdfId = String(data?.pdf_id || baseBrdId).trim();
      rememberHistoryLoadedBrd(nextPdfId);
      const nextPdf = {
        ...(selectedPreviousPdf && typeof selectedPreviousPdf === 'object' ? selectedPreviousPdf : {}),
        ...data,
        pdf_id: nextPdfId,
        pdf_file: data?.pdf_file || selectedFile.name,
      };

      setPdfId(nextPdfId);
      setDocumentName(resolveDocumentName(data, selectedFile, selectedPreviousPdf) || selectedFile.name || documentName || '');
      setIsReprocessed(false);
      setSelectedPreviousPdf(nextPdf);
      setDashboardPdf(nextPdf);
      setDashboardData(null);
      setDashboardError(null);
      setBrdLoadedFromHistory(true);
      setBrdChangeAnalysis(data.change_analysis || (data.change_summary ? { summary: data.change_summary } : null));
      setBrdUpdatePending(isPendingBrdUpdatePayload(data));
      setSections([]);
      setRawSectionOutline([]);
      setSectionGrouping(null);
      setSelectedSection(null);
      setProcessingResult(null);
      setEditedTestCases([]);
      setOriginalTestCases([]);
      setHistoryScriptsByCaseId({});
      setSectionArtifactIndex({});
      setConfirmedReplacementSection(null);
      setPreAnalysisPlan(null);
      setApprovedCounts({ positive_count: null, negative_count: null });
      setApprovedFocus([]);
      setApprovedGlobalConstraints([]);
      setApprovedTestCaseFieldConfig(null);
      setBulkMode('single');
      setBulkSelectedSections([]);
      setBulkJob(null);
      setBulkItems([]);
      setBulkEvents([]);
      setBulkError(null);
      setQueuedSingleGeneration(null);
      setQueuedPreAnalysis(null);
      clearReusableSections();
      setApprovalStatus({
        approval_required: false,
        approval_state: 'approved',
      });
      setApprovalEvents([]);
      await fetchAutomationBaseUrl(nextPdfId);
      await fetchApprovalStatus(nextPdfId);
      await fetchApprovalEvents(nextPdfId);
      if (canViewApprovalQueue) {
        await loadPendingApprovals();
      }
      setStatus('uploaded');
      setCurrentStep(2);
      return data;
    } catch (err) {
      const message = err?.message || 'Failed to upload updated BRD';
      setError('Failed to upload updated BRD: ' + message);
      setStatus('uploaded');
      throw new Error(message);
    } finally {
      setActiveUploadSource(null);
    }
  };

  const uploadTextImages = async () => {
    const normalizedText = String(manualInputText || '').trim();
    const sourceValidation = validateManualRequirementInput(normalizedText, manualInputTitle);
    if (!sourceValidation.valid) {
      setError(sourceValidation.message);
      return;
    }

    const imageValidation = validateManualInputImages(manualInputImages);
    if (!imageValidation.valid) {
      setManualInputImages(imageValidation.files);
      setError(imageValidation.message);
      return;
    }

    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      setError('Please select an application before creating a source.');
      return;
    }

    setActiveUploadSource('text-images');
    setStatus('uploading');
    setError(null);

    try {
      const formData = new FormData();
      formData.append('text', normalizedText);
      if (String(manualInputTitle || '').trim()) {
        formData.append('title', String(manualInputTitle || '').trim());
      }
      manualInputImages.forEach((image) => {
        formData.append('images', image);
      });

      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.UPLOAD_TEXT_IMAGES}?application_id=${encodeURIComponent(normalizedWorkspaceId)}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to create source'));
      }

      let data = await response.json();
      if (data?.job?.id) {
        const queuedJob = announceJob(data.job);
        const stagedPdfId = data.pdf_id;
        setPdfId(stagedPdfId);
        setStatus('extracting');
        await waitForJobResult(queuedJob);
        const sectionsResponse = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.SECTIONS}/${stagedPdfId}`);
        if (!sectionsResponse.ok) throw new Error(await parseApiError(sectionsResponse, 'Failed to restore text requirements'));
        data = await sectionsResponse.json();
      }
      const sourceTitle = manualInputTitle?.trim() || 'Manual Input';
      clearHistoryLoadedBrd();
      setPdfId(data.pdf_id);
      setFile({ name: sourceTitle });
      setDocumentName(resolveDocumentName(data, sourceTitle) || sourceTitle);
      setSelectedPreviousPdf(null);
      setBrdLoadedFromHistory(false);
      setBrdChangeAnalysis(null);
      setBrdUpdatePending(false);
      setIsReprocessed(false);
      applySectionsResponse(data);
      setSelectedSection(null);
      setProcessingResult(null);
      setEditedTestCases([]);
      setOriginalTestCases([]);
      setHistoryScriptsByCaseId({});
      setSectionArtifactIndex({});
      setConfirmedReplacementSection(null);
      setDgsConfig(null);
      setBulkMode('single');
      setBulkSelectedSections([]);
      setBulkJob(null);
      setBulkItems([]);
      setBulkEvents([]);
      setBulkError(null);
      setQueuedSingleGeneration(null);
      setQueuedPreAnalysis(null);
      resetProcessingMode();
      setApprovalStatus({
        approval_required: false,
        approval_state: 'approved',
      });
      setApprovalEvents([]);
      await loadPdfArtifactsDashboard(data.pdf_id, { updateSectionIndex: true });
      setStatus('sections_ready');
      setCurrentStep(3);
      await fetchApprovalStatus(data.pdf_id);
      await fetchApprovalEvents(data.pdf_id);
      if (canViewApprovalQueue) {
        await loadPendingApprovals();
      }
      return data;
    } catch (err) {
      setError(err?.message || "We couldn't create this source. Please review the details and try again.");
      setStatus('idle');
      return null;
    } finally {
      setActiveUploadSource(null);
    }
  };


  const uploadOpenApi = async () => {
    if (!openApiFile) {
      setError('Please select an OpenAPI or Swagger file');
      return;
    }
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      setError('Please select an application before uploading.');
      return;
    }

    setActiveUploadSource('openapi');
    setStatus('uploading');
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', openApiFile);

      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.UPLOAD_OPENAPI}?application_id=${encodeURIComponent(normalizedWorkspaceId)}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to upload OpenAPI source'));
      }

      let data = await response.json();
      if (data?.job?.id) {
        const queuedJob = announceJob(data.job);
        const stagedPdfId = data.pdf_id;
        setPdfId(stagedPdfId);
        setStatus('extracting');
        await waitForJobResult(queuedJob);
        const sectionsResponse = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.SECTIONS}/${stagedPdfId}`);
        if (!sectionsResponse.ok) throw new Error(await parseApiError(sectionsResponse, 'Failed to restore OpenAPI operations'));
        data = await sectionsResponse.json();
      }
      const sourceTitle =
        data?.openapi?.title
        || data?.source_title
        || openApiFile.name
        || 'OpenAPI Source';
      clearHistoryLoadedBrd();
      setPdfId(data.pdf_id);
      setFile(openApiFile);
      setDocumentName(resolveDocumentName(data, sourceTitle) || sourceTitle);
      setSelectedPreviousPdf(null);
      setBrdLoadedFromHistory(false);
      setBrdChangeAnalysis(null);
      setBrdUpdatePending(false);
      setIsReprocessed(false);
      applySectionsResponse(data);
      setSelectedSection(null);
      setProcessingResult(null);
      setEditedTestCases([]);
      setOriginalTestCases([]);
      setHistoryScriptsByCaseId({});
      setSectionArtifactIndex({});
      setConfirmedReplacementSection(null);
      setPreAnalysisPlan(null);
      setApprovedCounts({ positive_count: null, negative_count: null });
      setApprovedFocus([]);
      setApprovedGlobalConstraints([]);
      setApprovedTestCaseFieldConfig(null);
      setDgsConfig(null);
      setBulkMode('single');
      setBulkSelectedSections([]);
      setBulkJob(null);
      setBulkItems([]);
      setBulkEvents([]);
      setBulkError(null);
      setQueuedSingleGeneration(null);
      setQueuedPreAnalysis(null);
      clearReusableSections();
      resetProcessingMode();
      setApprovalStatus({
        approval_required: false,
        approval_state: 'approved',
      });
      setApprovalEvents([]);
      await loadPdfArtifactsDashboard(data.pdf_id, { updateSectionIndex: true });
      setStatus('sections_ready');
      setCurrentStep(3);
      await fetchApprovalStatus(data.pdf_id);
      await fetchApprovalEvents(data.pdf_id);
      if (canViewApprovalQueue) {
        await loadPendingApprovals();
      }
    } catch (err) {
      setError(err?.message || "We couldn't upload this OpenAPI source. Please review the file and try again.");
      setStatus('idle');
    } finally {
      setActiveUploadSource(null);
    }
  };

  const extractSections = async () => {
    const normalizedExtractionMode = pdfExtractionMode === 'features'
      ? 'features'
      : 'auto';
    setStatus('extracting');
    setError(null);

    try {
      const extractionQuery = new URLSearchParams({ extraction_mode: normalizedExtractionMode }).toString();
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.EXTRACT_SECTIONS}/${pdfId}?${extractionQuery}`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(await parseApiError(response, SECTION_BUILD_ERROR_MESSAGE));
      }

      const submitted = await response.json();
      if (submitted?.job?.id) {
        extractionJobIdRef.current = String(submitted.job.id);
        announceJob(submitted.job);
        setStatus('extracting');
        return submitted;
      }
      const data = submitted;
      const nextChangeAnalysis = data.change_analysis || (data.change_summary ? { summary: data.change_summary } : null);
      applySectionsResponse(data);
      setBrdChangeAnalysis(nextChangeAnalysis);
      setBrdUpdatePending(isPendingBrdUpdatePayload(data));
      setHistoryScriptsByCaseId({});
      setConfirmedReplacementSection(null);
      setBulkSelectedSections([]);
      setBulkJob(null);
      setBulkItems([]);
      setBulkEvents([]);
      setBulkError(null);
      setQueuedSingleGeneration(null);
      setQueuedPreAnalysis(null);
      clearReusableSections();
      await loadPdfArtifactsDashboard(pdfId, { updateSectionIndex: true });
      setProcessingMode('pdf-only');
      setStatus('sections_ready');
      setCurrentStep(isTocStructureReviewRequired(nextChangeAnalysis) ? 2 : 3);
      await fetchApprovalStatus(pdfId);
      await fetchApprovalEvents(pdfId);
      return data;
    } catch (err) {
      const message = toUserFacingApiError(err.message, SECTION_BUILD_ERROR_MESSAGE);
      setError(message);
      setStatus('uploaded');
      return null;
    }
  };

  useEffect(() => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId) return undefined;
    let disposed = false;
    // Follow extraction only when output is missing or a fresh extraction is
    // underway. A saved document with sections must keep its requested route.
    const isAwaitingExtraction = () => {
      const step = Number(currentStepRef.current);
      return !disposed
        && pdfHydrationsInFlightRef.current === 0
        && (!hasSectionsRef.current || statusRef.current === 'extracting')
        && step <= 3;
    };

    const restoreCompletedExtraction = async (job) => {
      if (
        !isAwaitingExtraction()
        || String(job?.type || '') !== 'document_extraction'
        || String(job?.source_id || '') !== normalizedPdfId
        || (hasSectionsRef.current && String(job?.id || '') !== extractionJobIdRef.current)
      ) return;
      if (job.status === 'failed' || job.status === 'cancelled') {
        setError(job?.error?.message || 'Document extraction did not complete. Retry it from the job center.');
        setStatus('uploaded');
        return;
      }
      if (job.status !== 'completed' && job.status !== 'partial_completed') {
        setStatus('extracting');
        return;
      }
      try {
        const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.SECTIONS}/${normalizedPdfId}`);
        if (!response.ok) throw new Error(await parseApiError(response, SECTION_BUILD_ERROR_MESSAGE));
        const data = await response.json();
        if (!isAwaitingExtraction()) return;
        const nextChangeAnalysis = data.change_analysis || (data.change_summary ? { summary: data.change_summary } : null);
        applySectionsResponse(data);
        setBrdChangeAnalysis(nextChangeAnalysis);
        setBrdUpdatePending(isPendingBrdUpdatePayload(data));
        clearReusableSections();
        await loadPdfArtifactsDashboard(normalizedPdfId, { updateSectionIndex: true });
        if (!isAwaitingExtraction()) return;
        setProcessingMode('pdf-only');
        setStatus('sections_ready');
        setCurrentStep(isTocStructureReviewRequired(nextChangeAnalysis) ? 2 : 3);
        setError(null);
      } catch (restoreError) {
        if (!isAwaitingExtraction()) return;
        setError(toUserFacingApiError(restoreError.message, SECTION_BUILD_ERROR_MESSAGE));
        setStatus('uploaded');
      }
    };

    const onCompleted = (event) => { void restoreCompletedExtraction(event.detail); };
    window.addEventListener('testsuite:job-completed', onCompleted);
    listJobs({ source_id: normalizedPdfId, type: 'document_extraction', limit: 5 })
      .then((jobs) => {
        const latest = jobs[0];
        if (latest) void restoreCompletedExtraction(latest);
      })
      .catch(() => {});
    return () => {
      disposed = true;
      window.removeEventListener('testsuite:job-completed', onCompleted);
    };
  // The callbacks below intentionally read the latest workflow closure when the
  // selected source changes; job state itself is authoritative across refreshes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfId]);

  const restoreSkippedTocSections = async (rowIds = []) => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId) {
      throw new Error('Please select a BRD before restoring skipped TOC sections.');
    }

    const normalizedRowIds = Array.isArray(rowIds)
      ? rowIds
        .map((rowId) => String(rowId || '').trim())
        .filter(Boolean)
      : [];
    if (normalizedRowIds.length === 0) {
      throw new Error('Select at least one skipped TOC section.');
    }

    setError(null);
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.PDF_SECTIONS_ADD_FROM_OUTLINE(normalizedPdfId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ row_ids: normalizedRowIds }),
      }
    );

    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to restore skipped TOC sections'));
    }

    const data = await response.json();
    applySectionsResponse(data);
    await loadPdfArtifactsDashboard(normalizedPdfId, { updateSectionIndex: true });
    return data;
  };

  const removeRestoredTocSections = async (rowIds = []) => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId) {
      throw new Error('Please select a BRD before removing restored TOC sections.');
    }

    const normalizedRowIds = Array.isArray(rowIds)
      ? rowIds
        .map((rowId) => String(rowId || '').trim())
        .filter(Boolean)
      : [];
    if (normalizedRowIds.length === 0) {
      throw new Error('Select at least one manually restored TOC section.');
    }

    setError(null);
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.PDF_SECTIONS_REMOVE_FROM_OUTLINE(normalizedPdfId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ row_ids: normalizedRowIds }),
      }
    );

    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to remove restored TOC sections'));
    }

    const data = await response.json();
    applySectionsResponse(data);
    await loadPdfArtifactsDashboard(normalizedPdfId, { updateSectionIndex: true });
    return data;
  };

  const applyRebuiltToc = async ({ confirmReplaceExisting = false } = {}) => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId) {
      throw new Error('Please select a BRD before applying a rebuilt TOC.');
    }

    setStatus('extracting');
    setError(null);
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.PDF_REBUILD_TOC_APPLY(normalizedPdfId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ confirm_replace_existing: Boolean(confirmReplaceExisting) }),
      }
    );

    if (!response.ok) {
      const err = new Error(await parseApiError(response, SECTION_BUILD_ERROR_MESSAGE));
      err.status = response.status;
      setStatus('sections_ready');
      throw err;
    }

    const data = await response.json();
    applySectionsResponse(data);
    setSelectedSection(null);
    setProcessingResult(null);
    setEditedTestCases([]);
    setOriginalTestCases([]);
    setHistoryScriptsByCaseId({});
    setSectionArtifactIndex({});
    setConfirmedReplacementSection(null);
    setPreAnalysisPlan(null);
    setBulkMode('single');
    setBulkSelectedSections([]);
    setBulkJob(null);
    setBulkItems([]);
    setBulkEvents([]);
    setBulkError(null);
    setQueuedSingleGeneration(null);
    setQueuedPreAnalysis(null);
    clearReusableSections();
    await loadPdfArtifactsDashboard(normalizedPdfId, { updateSectionIndex: true });
    setProcessingMode('pdf-only');
    setStatus('sections_ready');
    setCurrentStep(3);
    await fetchApprovalStatus(normalizedPdfId);
    await fetchApprovalEvents(normalizedPdfId);
    return data;
  };

  const useReusableSections = async () => {
    if (!reusableSections.available || reusableSections.stale) {
      await extractSections();
      return;
    }

    setError(null);
    setStatus('sections_ready');
    setCurrentStep(isTocStructureReviewRequired(brdChangeAnalysis) ? 2 : 3);
    await fetchApprovalStatus(pdfId);
    await fetchApprovalEvents(pdfId);
    if (canViewApprovalQueue) {
      await loadPendingApprovals();
    }
  };

  const returnToExtractionStep = useCallback(() => {
    const restoredSectionsCount = Array.isArray(sections) ? sections.length : 0;
    if (restoredSectionsCount > 0) {
      setReusableSections({
        available: true,
        stale: false,
        count: restoredSectionsCount,
        extractionMethod: reusableSections.extractionMethod || '',
        itemType: reusableSections.itemType || '',
      });
      setStatus('uploaded');
    }
    setCurrentStep(2);
  }, [reusableSections.extractionMethod, reusableSections.itemType, sections]);

  const processSection = async (counts = null) => {
    if (!selectedSection) {
      setError('Please select a section');
      return;
    }

    const hasExistingArtifacts = Boolean(sectionArtifactIndex?.[selectedSection]?.has_generated);
    const replaceExisting = hasExistingArtifacts
      ? (Boolean(counts?.replace_existing) || confirmedReplacementSection === selectedSection)
      : false;
    if (hasExistingArtifacts && !replaceExisting) {
      setError('Confirm regeneration to create a new version for this section.');
      return;
    }

    const sectionRequestPayload = {
      pdf_id: pdfId,
      section_number: selectedSection,
      include_child_sections: true,
      positive_count: counts?.positive_count ?? approvedCounts.positive_count,
      negative_count: counts?.negative_count ?? approvedCounts.negative_count,
      additional_focus: counts?.additional_focus ?? approvedFocus,
      global_constraints: counts?.global_constraints ?? approvedGlobalConstraints,
      dgs_validation_rules: counts?.dgs_validation_rules ?? dgsConfig?.rules ?? [],
      input_set_count: counts?.input_set_count ?? 3,
      test_case_field_config: counts?.test_case_field_config ?? approvedTestCaseFieldConfig,
      replace_existing: replaceExisting,
      defer_enrichment: true,
    };

    const selectedSectionData = (Array.isArray(sections) ? sections : []).find(
      (section) => Number(section?.number) === Number(selectedSection)
    );
    const sectionTitle =
      selectedSectionData?.title
      || sectionArtifactIndex?.[selectedSection]?.section_title
      || preAnalysisPlan?.section_title
      || `Section ${selectedSection}`;
    const initialApprovedCounts = {
      positive_count: Math.max(0, Number(sectionRequestPayload.positive_count || 0)),
      negative_count: Math.max(0, Number(sectionRequestPayload.negative_count || 0)),
    };
    const initialStreamResult = {
      pdf_id: pdfId,
      section_number: selectedSection,
      section_title: sectionTitle,
      status: 'running',
      stream_status: 'running',
      approved_counts: initialApprovedCounts,
      approved_focus: sectionRequestPayload.additional_focus,
      approved_global_constraints: sectionRequestPayload.global_constraints,
      test_case_field_config: sectionRequestPayload.test_case_field_config,
      input_set_count: sectionRequestPayload.input_set_count,
      include_child_sections: sectionRequestPayload.include_child_sections,
      dgs_rules_count: Array.isArray(sectionRequestPayload.dgs_validation_rules)
        ? sectionRequestPayload.dgs_validation_rules.length
        : 0,
      test_cases: buildInitialStreamTestCases(sectionTitle, sectionRequestPayload.test_case_field_config),
      streamed_case_count: 0,
      streamed_input_sets_count: 0,
    };

    bulkStartInFlightRef.current = true;
    setStatus('processing');
    setError(null);
    setBulkError(null);
    setBulkJob(null);
    setBulkItems([]);
    setBulkEvents([]);
    setQueuedSingleGeneration(null);
    setQueuedPreAnalysis(null);
    setBulkMode('single');
    setBulkSelectedSections([selectedSection]);
    setProcessingResult(initialStreamResult);
    setEditedTestCases([]);
    setOriginalTestCases([]);
    setApiResponse(null);
    setConfirmedReplacementSection(null);
    setCurrentStep(4);

    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATIONS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...sectionRequestPayload,
          section_numbers: [selectedSection],
          mode: 'pdf',
          append_only: false,
        }),
      });
      if (!response.ok) throw new Error(await parseApiError(response, 'Failed to start section generation'));
      const payload = await response.json();
      const createdJob = payload?.job || payload?.bulk_job;
      if (!createdJob?.id) throw new Error('Generation queue did not return a job id.');
      announceJob(createdJob);
      setBulkJob(createdJob);
      setQueuedSingleGeneration({
        bulk_job_id: createdJob.id,
        pdf_id: pdfId,
        section_number: selectedSection,
      });
      await fetchBulkGenerationSnapshot(createdJob.id, { includeEvents: true });
      return;
    } catch (err) {
      const message = err?.message || 'Failed to start section generation';
      setError(message);
      setBulkError(message);
      setStatus('analysis_ready');
      setCurrentStep(3);
      return;
    } finally {
      bulkStartInFlightRef.current = false;
    }

  };

  const generateAdditionalTestCases = async ({
    positive_count = 0,
    negative_count = 0,
    additional_focus = [],
    global_constraints = approvedGlobalConstraints,
    existing_test_cases = null,
    test_case_field_config = approvedTestCaseFieldConfig,
  } = {}) => {
    if (!selectedSection) {
      const msg = 'Please select a section';
      setError(msg);
      throw new Error(msg);
    }

    const additionalPositive = Math.max(0, Number(positive_count || 0));
    const additionalNegative = Math.max(0, Number(negative_count || 0));
    if (additionalPositive + additionalNegative <= 0) {
      throw new Error('Add at least one additional positive or negative test case.');
    }

    setStatus('bulk_processing');
    setError(null);
    setBulkError(null);
    setBulkJob(null);
    setBulkItems([]);
    setBulkEvents([]);
    setQueuedSingleGeneration(null);
    setQueuedPreAnalysis(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATIONS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdf_id: pdfId,
          section_numbers: [selectedSection],
          mode: 'pdf',
          include_child_sections: true,
          positive_count: additionalPositive,
          negative_count: additionalNegative,
          additional_focus,
          global_constraints,
          existing_test_cases,
          dgs_validation_rules: dgsConfig?.rules ?? [],
          input_set_count: 3,
          test_case_field_config,
          append_only: true,
          replace_existing: false,
          defer_enrichment: false,
        }),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to start additional test case generation'));
      }

      const generationPayload = await response.json();
      const createdJob = generationPayload?.bulk_job || null;
      const createdJobId = String(createdJob?.id || '').trim();
      if (!createdJobId) {
        throw new Error('Additional test case generation did not start.');
      }

      setBulkMode('single');
      setBulkSelectedSections([selectedSection]);
      setBulkJob(createdJob);

      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      const loadQueuedSnapshot = async () => {
        const [jobResponse, itemsResponse, eventsResponse] = await Promise.all([
          apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATION_BY_ID(createdJobId)}`),
          apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATION_ITEMS(createdJobId)}`),
          apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATION_EVENTS(createdJobId)}?limit=100`),
        ]);

        if (!jobResponse.ok) {
          throw new Error(await parseApiError(jobResponse, 'Failed to load additional generation status'));
        }
        if (!itemsResponse.ok) {
          throw new Error(await parseApiError(itemsResponse, 'Failed to load additional generation item'));
        }

        const jobPayload = await jobResponse.json();
        const itemsPayload = await itemsResponse.json();
        const eventsPayload = eventsResponse?.ok ? await eventsResponse.json().catch(() => ({})) : {};
        const nextJob = jobPayload?.bulk_job || null;
        const nextItems = Array.isArray(itemsPayload?.items) ? itemsPayload.items : [];
        setBulkJob(nextJob);
        setBulkItems(nextItems);
        setBulkEvents(Array.isArray(eventsPayload?.events) ? eventsPayload.events : []);

        if (!isBulkJobTerminal(normalizeBulkStatus(nextJob?.status))) {
          setStatus('bulk_processing');
        }
        return { job: nextJob, items: nextItems };
      };

      const timeoutAt = Date.now() + (45 * 60 * 1000);
      let data = null;
      while (!data) {
        const snapshot = await loadQueuedSnapshot();
        const jobStatus = normalizeBulkStatus(snapshot.job?.status);
        if (isBulkJobTerminal(jobStatus)) {
          const queuedItem = snapshot.items.find(
            (item) => Number(item?.section_number) === Number(selectedSection)
          );
          const itemStatus = normalizeBulkStatus(queuedItem?.status);
          if (itemStatus === 'completed' && queuedItem?.result_payload) {
            data = queuedItem.result_payload;
            break;
          }
          throw new Error(queuedItem?.error || 'Additional test case generation did not complete successfully.');
        }

        if (Date.now() > timeoutAt) {
          throw new Error('Timed out waiting for additional test case generation.');
        }
        await sleep(3000);
      }

      if (Array.isArray(data?.approved_global_constraints)) {
        setApprovedGlobalConstraints(normalizeUniqueStringList(data.approved_global_constraints));
      }
      if (data?.test_case_field_config && typeof data.test_case_field_config === 'object') {
        setApprovedTestCaseFieldConfig(data.test_case_field_config);
      }
      setStatus('completed');
      return data;
    } catch (err) {
      setError('Failed to generate additional test cases: ' + err.message);
      setStatus('completed');
      throw err;
    }
  };

  const preAnalyzeSection = async ({ replaceExisting = false, sectionNumber = null } = {}) => {
    const targetSection = Number(sectionNumber || selectedSection);
    if (!Number.isFinite(targetSection) || targetSection <= 0) {
      setError('Please select a section');
      return;
    }

    if (targetSection !== Number(selectedSection)) {
      setSelectedSection(targetSection);
    }
    const hasExistingArtifacts = Boolean(sectionArtifactIndex?.[targetSection]?.has_generated);
    setConfirmedReplacementSection(replaceExisting ? targetSection : null);
    setStatus('bulk_processing');
    setError(null);
    setBulkError(null);
    setBulkJob(null);
    setBulkItems([]);
    setBulkEvents([]);
    setQueuedSingleGeneration(null);
    setQueuedPreAnalysis(null);

    try {
      const endpoint = `${API_BASE_URL}${API_ENDPOINTS.PRE_ANALYZE_SECTION_JOB}`;

      const payload = {
        pdf_id: pdfId,
        section_number: targetSection,
        include_child_sections: true,
        global_constraints: approvedGlobalConstraints,
        dgs_validation_rules: dgsConfig?.rules ?? [],
        replace_existing: replaceExisting,
      };

      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to prepare analysis plan'));
      }

      const data = await response.json();
      const createdJob = data?.bulk_job || null;
      const createdJobId = String(createdJob?.id || '').trim();
      if (!createdJobId) {
        throw new Error('Queue did not return a pre-analysis job id');
      }
      setBulkMode('single');
      setBulkSelectedSections([targetSection]);
      setBulkJob(createdJob);
      setQueuedPreAnalysis(
        {
          bulk_job_id: createdJobId,
          pdf_id: pdfId,
          section_number: targetSection,
          replace_existing: replaceExisting,
          had_existing_artifacts: hasExistingArtifacts,
          global_constraints: approvedGlobalConstraints,
        }
      );
      setCurrentStep(3);
      await fetchBulkGenerationSnapshot(createdJobId, { includeEvents: true });
      await fetchApprovalStatus(pdfId);
    } catch (err) {
      setConfirmedReplacementSection(null);
      setError('Failed to prepare analysis plan: ' + err.message);
      setBulkError(err?.message || 'Failed to prepare analysis plan');
      setStatus('sections_ready');
    }
  };

  const toggleBulkSectionSelection = (sectionNumber) => {
    const normalized = Number(sectionNumber);
    if (!Number.isFinite(normalized) || normalized <= 0) return;
    setBulkSelectedSections((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const exists = current.some((value) => Number(value) === normalized);
      if (exists) {
        return current.filter((value) => Number(value) !== normalized);
      }
      return [...current, normalized];
    });
  };

  const selectAllBulkSections = () => {
    const allSections = (Array.isArray(sections) ? sections : [])
      .map((section) => Number(section?.number))
      .filter((value) => Number.isFinite(value) && value > 0);
    setBulkSelectedSections(Array.from(new Set(allSections)));
  };

  const clearBulkSelection = () => {
    setBulkSelectedSections([]);
  };

  const fetchBulkGenerationSnapshot = useCallback(async (bulkJobId, { includeEvents = false } = {}) => {
    const normalizedJobId = String(bulkJobId || '').trim();
    if (!normalizedJobId) return null;

    const requests = [
      apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATION_BY_ID(normalizedJobId)}`),
      apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATION_ITEMS(normalizedJobId)}`),
    ];
    if (includeEvents) {
      requests.push(apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATION_EVENTS(normalizedJobId)}?limit=100`));
    }

    const responses = await Promise.all(requests);
    const [jobResponse, itemsResponse, eventsResponse] = responses;

    if (!jobResponse.ok) {
      throw new Error(await parseApiError(jobResponse, 'Failed to load generation progress'));
    }
    if (!itemsResponse.ok) {
      throw new Error(await parseApiError(itemsResponse, 'Failed to load generation progress'));
    }

    const jobPayload = await jobResponse.json();
    const itemsPayload = await itemsResponse.json();
    const nextJob = jobPayload?.bulk_job || null;
    const nextItems = Array.isArray(itemsPayload?.items) ? itemsPayload.items : [];
    setBulkJob(nextJob);
    setBulkItems(nextItems);
    if (includeEvents && eventsResponse?.ok) {
      const eventsPayload = await eventsResponse.json().catch(() => ({}));
      setBulkEvents(Array.isArray(eventsPayload?.events) ? eventsPayload.events : []);
    }

    const statusValue = normalizeBulkStatus(nextJob?.status);
    if (isBulkJobTerminal(statusValue)) {
      setStatus('sections_ready');
    } else {
      setStatus('bulk_processing');
    }
    return nextJob;
  }, [isBulkJobTerminal, normalizeBulkStatus, parseApiError]);

  const restoreBulkGenerationById = useCallback(async (bulkJobId, { expectedPdfId = null } = {}) => {
    const normalizedJobId = String(bulkJobId || '').trim();
    if (!normalizedJobId || bulkStartInFlightRef.current) return null;
    const normalizedExpectedPdfId = String(expectedPdfId || '').trim();

    setBulkLoading(true);
    setBulkError(null);
    try {
      const restoredJob = await fetchBulkGenerationSnapshot(normalizedJobId, { includeEvents: true });
      if (
        restoredJob
        && normalizedExpectedPdfId
        && String(restoredJob.pdf_id || '').trim()
        && String(restoredJob.pdf_id || '').trim() !== normalizedExpectedPdfId
      ) {
        throw new Error('Generation job does not belong to this BRD.');
      }
      const restoredSections = Array.isArray(restoredJob?.section_numbers)
        ? restoredJob.section_numbers
        : [];
      setBulkSelectedSections(
        restoredSections
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      );
      return restoredJob;
    } catch (err) {
      setBulkJob(null);
      setBulkItems([]);
      setBulkEvents([]);
      setBulkError(err?.message || 'Failed to load generation progress');
      return null;
    } finally {
      setBulkLoading(false);
    }
  }, [fetchBulkGenerationSnapshot]);

  const restoreLatestBulkGeneration = useCallback(async (targetPdfId = pdfId) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId || bulkStartInFlightRef.current) return null;

    setBulkLoading(true);
    setBulkError(null);
    try {
      const query = new URLSearchParams({
        pdf_id: normalizedPdfId,
        limit: '10',
      }).toString();
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATIONS}?${query}`);
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load latest generation'));
      }

      const payload = await response.json().catch(() => ({}));
      const jobs = Array.isArray(payload?.items) ? payload.items : [];
      const latestGenerationJob = jobs.find((job) => {
        const mode = String(job?.mode || '').trim().toLowerCase();
        const type = String(job?.type || '').trim().toLowerCase();
        return mode === 'pdf'
          || type === 'test_case_generation' || type === 'test_case_regeneration';
      });

      if (!latestGenerationJob?.id) {
        setBulkJob(null);
        setBulkItems([]);
        setBulkEvents([]);
        setBulkError('No generation job was found for this BRD. Start generation from section selection.');
        setStatus('sections_ready');
        return null;
      }

      const restoredSections = Array.isArray(latestGenerationJob.section_numbers)
        ? latestGenerationJob.section_numbers
        : [];
      setBulkSelectedSections(
        restoredSections
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      );
      return await restoreBulkGenerationById(latestGenerationJob.id, { expectedPdfId: normalizedPdfId });
    } catch (err) {
      setBulkError(err?.message || 'Failed to load latest generation');
      return null;
    } finally {
      setBulkLoading(false);
    }
  }, [parseApiError, pdfId, restoreBulkGenerationById]);

  const startBulkGeneration = async () => {
    if (!pdfId) {
      setBulkError('Please upload and extract a BRD first.');
      return;
    }

    const selected = Array.from(
      new Set(
        (Array.isArray(bulkSelectedSections) ? bulkSelectedSections : [])
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      )
    );

    if (selected.length === 0) {
      setBulkError('Select at least one section to generate.');
      return;
    }

    bulkStartInFlightRef.current = true;
    setBulkLoading(true);
    setBulkError(null);
    setError(null);
    setStatus('bulk_processing');
    setSelectedSection(null);
    setCurrentStep(4);
    setBulkItems([]);
    setBulkEvents([]);
    setQueuedSingleGeneration(null);
    setQueuedPreAnalysis(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATIONS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdf_id: pdfId,
          section_numbers: selected,
          mode: 'pdf',
          positive_count: approvedCounts?.positive_count,
          negative_count: approvedCounts?.negative_count,
          additional_focus: approvedFocus,
          global_constraints: approvedGlobalConstraints,
          dgs_validation_rules: dgsConfig?.rules ?? [],
          input_set_count: 3,
          test_case_field_config: approvedTestCaseFieldConfig,
          include_child_sections: true,
          replace_existing: true,
          defer_enrichment: true,
        }),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to start generation'));
      }

      const data = await response.json();
      const createdJob = data?.bulk_job || null;
      setBulkJob(createdJob);
      const createdJobId = String(createdJob?.id || '').trim();
      if (createdJobId) {
        await fetchBulkGenerationSnapshot(createdJobId, { includeEvents: true });
      }
    } catch (err) {
      setBulkError(err?.message || 'Failed to start generation');
      setStatus('sections_ready');
      setCurrentStep(3);
    } finally {
      bulkStartInFlightRef.current = false;
      setBulkLoading(false);
    }
  };

  const cancelBulkGeneration = async (reason = null) => {
    const bulkJobId = String(bulkJob?.id || '').trim();
    if (!bulkJobId) return;
    setBulkLoading(true);
    setBulkError(null);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.BULK_GENERATION_CANCEL(bulkJobId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: reason || '' }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to cancel generation'));
      }
      await fetchBulkGenerationSnapshot(bulkJobId, { includeEvents: true });
    } catch (err) {
      setBulkError(err?.message || 'Failed to cancel generation');
    } finally {
      setBulkLoading(false);
    }
  };

  const retryFailedGenerationSections = async () => {
    const jobId = String(bulkJob?.id || '').trim();
    if (!jobId) return;
    setBulkLoading(true);
    setBulkError(null);
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.JOB_RETRY(jobId)}`, { method: 'POST' });
      if (!response.ok) throw new Error(await parseApiError(response, 'Failed to retry failed sections'));
      const payload = await response.json();
      const nextJob = payload?.job || bulkJob;
      setBulkJob(nextJob);
      setStatus('bulk_processing');
      await fetchBulkGenerationSnapshot(jobId, { includeEvents: true });
    } catch (err) {
      setBulkError(err?.message || 'Failed to retry failed sections');
    } finally {
      setBulkLoading(false);
    }
  };

  useEffect(() => {
    const bulkJobId = String(bulkJob?.id || '').trim();
    if (!bulkJobId) return undefined;
    const statusValue = normalizeBulkStatus(bulkJob?.status);
    if (isBulkJobTerminal(statusValue)) return undefined;

    let isActive = true;
    let inFlight = false;
    const poll = async () => {
      if (!isActive || inFlight) return;
      inFlight = true;
      try {
        await fetchBulkGenerationSnapshot(bulkJobId, { includeEvents: true });
      } catch (err) {
        if (isActive) {
          setBulkError(err?.message || 'Failed to refresh generation progress');
        }
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(() => {
      void poll();
    }, 3000);
    void poll();

    return () => {
      isActive = false;
      clearInterval(timer);
    };
  }, [bulkJob?.id, bulkJob?.status, fetchBulkGenerationSnapshot, isBulkJobTerminal, normalizeBulkStatus]);

  const parseDgsSheet = async (file) => {
    if (!file) {
      throw new Error('Please select a DGS sheet to upload.');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.PARSE_DGS_SHEET}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.detail || 'Failed to parse DGS sheet');
    }

    const data = await response.json();
    setDgsConfig(data);
    return data;
  };

  const downloadDgsTemplate = async () => {
    const endpoint = `${API_BASE_URL}${API_ENDPOINTS.DOWNLOAD_DGS_TEMPLATE}`;
    const separator = endpoint.includes('?') ? '&' : '?';
    const downloadUrl = `${endpoint}${separator}t=${Date.now()}`;

    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = 'dgs_validation_template.xlsx';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const downloadExtractedSectionsJson = useCallback((targetPdfId = pdfId) => {
    const normalizedPdfId = String(targetPdfId || '').trim();
    if (!normalizedPdfId) {
      setError('Please select a BRD before downloading extracted section JSON.');
      return;
    }

    const endpoint = `${API_BASE_URL}${API_ENDPOINTS.EXTRACTED_SECTIONS_JSON(normalizedPdfId)}`;
    const separator = endpoint.includes('?') ? '&' : '?';
    const downloadUrl = `${endpoint}${separator}t=${Date.now()}`;

    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = `${normalizedPdfId}_extracted_sections_tracking.json`;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }, [pdfId]);

  const openUploadedPdf = useCallback((targetPdf = null) => {
    const normalizedPdfId = String(
      (typeof targetPdf === 'string' ? targetPdf : targetPdf?.pdf_id) || pdfId || ''
    ).trim();
    if (!normalizedPdfId) {
      setError('Please select a BRD first.');
      return;
    }

    const endpoint = `${API_BASE_URL}${API_ENDPOINTS.PDF_SOURCE_FILE(normalizedPdfId)}`;
    const separator = endpoint.includes('?') ? '&' : '?';
    const viewUrl = `${endpoint}${separator}t=${Date.now()}`;
    window.open(viewUrl, '_blank', 'noopener,noreferrer');
  }, [pdfId]);

  const clearDgsConfig = () => {
    setDgsConfig(null);
  };

  /**
   * Extract test cases from processingResult
   * Handles both structures:
   * 1. Flat array: data.test_cases = [...]
   * 2. Nested: data.test_cases = {positive: [...], negative: [...]}
   */
  const extractTestCases = (data) => {
    if (!data || !data.test_cases) return [];

    if (Array.isArray(data.test_cases)) {
      return data.test_cases;
    }

    if (hasCategorizedTestCases(data.test_cases)) {
      const cases = {
        positive: [],
        negative: [],
      };
      
      if (data.test_cases.positive) {
        const positiveCases = Array.isArray(data.test_cases.positive)
          ? data.test_cases.positive
          : Object.values(data.test_cases.positive);
        cases.positive.push(
          ...positiveCases.map(tc => ({
            ...tc,
          }))
        );
      }

      if (data.test_cases.negative) {
        const negativeCases = Array.isArray(data.test_cases.negative)
          ? data.test_cases.negative
          : Object.values(data.test_cases.negative);
        cases.negative.push(
          ...negativeCases.map(tc => ({
            ...tc,
          }))
        );
      }
      
      return cases;
    }

    if (typeof data.test_cases === 'object') {
      return [data.test_cases];
    }

    return [];
  };

  const loadPreviousPDFs = async () => {
    setShowPreviousPDFs(true);
    setPreviousPDFsLoading(true);
    setPreviousPDFsError(null);
    setHistoryRestoreLoading(null);
    setPreviousPDFs([]);
    setDashboardPdf(null);
    setDashboardData(null);
    setDashboardError(null);
    setSectionArtifactIndex({});
    setBrdSharingStatus(null);
    setBrdSharingRequests([]);

    let pdfs = [];
    try {
      const query = new URLSearchParams({ source_category: historySourceCategory }).toString();
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.PROCESSED_PDFS}?${query}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load history');
      }
      const data = await response.json();
      const rows = Array.isArray(data.pdfs) ? data.pdfs : [];
      pdfs = rows.filter((row) => {
        const sourceType = String(row?.source_type || '').trim().toLowerCase();
        const pdfRowId = String(row?.pdf_id || '').trim().toLowerCase();
        const isFeatureCapture = sourceType === 'feature_capture' || pdfRowId.startsWith('feature_capture_');
        const isManualInput = sourceType === 'manual_input' || sourceType === 'manual_upload';
        if (historySourceCategory === 'requirements') {
          return isManualInput && !isFeatureCapture;
        }
        if (historySourceCategory === 'openapi') {
          return sourceType === 'openapi' && !isFeatureCapture;
        }
        if (historySourceCategory === 'testcase_excel') {
          return ['testcase_excel', 'test_case_excel', 'excel_test_cases'].includes(sourceType) && !isFeatureCapture;
        }
        return !isManualInput && !isFeatureCapture;
      });
      setPreviousPDFs(pdfs);
    } catch (err) {
      const message = `Failed to load previous ${historySourceListLabel}: ` + (err?.message || err);
      setPreviousPDFs([]);
      setDashboardPdf(null);
      setDashboardData(null);
      setDashboardError(null);
      setSectionArtifactIndex({});
      setBrdSharingStatus(null);
      setBrdSharingRequests([]);
      setPreviousPDFsError(message);
      setError(message);
      return;
    } finally {
      setPreviousPDFsLoading(false);
    }

    const firstPdf = pdfs[0] || null;
    setDashboardPdf(firstPdf);
    if (firstPdf?.application_id) {
      setSelectedWorkspaceId(String(firstPdf.application_id));
    }
    if (firstPdf?.pdf_id) {
      const dashboardLoaders = [loadPdfArtifactsDashboard(firstPdf.pdf_id)];
      if (historySourceCategory === 'brd') {
        dashboardLoaders.push(loadBrdSharingForPdf(firstPdf.pdf_id));
      }
      await Promise.allSettled(dashboardLoaders);
    } else {
      setDashboardData(null);
      setDashboardError(null);
      setSectionArtifactIndex({});
      setBrdSharingStatus(null);
      setBrdSharingRequests([]);
    }
  };

  const loadPdfArtifactsDashboard = async (pdfId, { updateSectionIndex = false } = {}) => {
    if (!pdfId) {
      setDashboardData(null);
      setDashboardError(`No ${historySourceLabel} selected`);
      if (updateSectionIndex) {
        setSectionArtifactIndex({});
      }
      return;
    }

    setDashboardLoading(true);
    setDashboardError(null);

    try {
      const endpoint = `${API_ENDPOINTS.PDF_ARTIFACT_DASHBOARD(pdfId)}?generated_only=true`;
      const response = await apiFetch(`${API_BASE_URL}${endpoint}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load dashboard data');
      }
      const data = await response.json();
      let versionHistory = null;
      try {
        const versionResponse = await apiFetch(
          `${API_BASE_URL}${API_ENDPOINTS.BRD_VERSION_HISTORY(pdfId)}`
        );
        if (versionResponse.ok) {
          versionHistory = await versionResponse.json();
        }
      } catch (_versionError) {
        versionHistory = null;
      }
      const nextData = {
        ...data,
        brd_versions: Array.isArray(versionHistory?.versions) ? versionHistory.versions : [],
        active_brd_version: versionHistory?.active_version ?? null,
      };
      setDashboardData(nextData);
      if (updateSectionIndex) {
        setSectionArtifactIndex(buildSectionArtifactIndex(nextData));
      }
      return nextData;
    } catch (err) {
      setDashboardData(null);
      if (updateSectionIndex) {
        setSectionArtifactIndex({});
      }
      setDashboardError(`Failed to load ${historySourceLabel} dashboard: ` + err.message);
      return null;
    } finally {
      setDashboardLoading(false);
    }
  };

  const previewPreviousPdf = async (pdf) => {
    if (!pdf?.pdf_id) return;
    setDashboardPdf(pdf);
    if (pdf?.application_id) {
      setSelectedWorkspaceId(String(pdf.application_id));
    }
    const dashboardLoaders = [loadPdfArtifactsDashboard(pdf.pdf_id)];
    if (historySourceCategory === 'brd') {
      dashboardLoaders.push(loadBrdSharingForPdf(pdf.pdf_id));
    }
    await Promise.all(dashboardLoaders);
  };

  // Puts a completed analysis plan back into the planning step. The plan was
  // requested for this section (after the regeneration confirmation when it
  // already had test cases), so generating from it may replace that output.
  const restorePreAnalysisPlan = (planResult, { sectionNumber } = {}) => {
    const suggested = planResult?.plan?.suggested || {};
    setPreAnalysisPlan(planResult);
    setApprovedCounts({
      positive_count: suggested.positive_count ?? 20,
      negative_count: suggested.negative_count ?? 12,
    });
    setApprovedFocus(
      Array.isArray(planResult?.plan?.coverage_focus)
        ? normalizeUniqueStringList(planResult.plan.coverage_focus)
        : []
    );
    setApprovedGlobalConstraints(
      normalizeUniqueStringList(
        planResult?.approved_global_constraints
        ?? planResult?.plan?.approved_global_constraints
        ?? []
      )
    );
    const normalizedSection = Number(sectionNumber);
    setConfirmedReplacementSection(
      Number.isFinite(normalizedSection) && normalizedSection > 0 ? normalizedSection : null
    );
  };

  const openSavedPreAnalysisPlan = async (sectionNumber) => {
    const targetSection = Number(sectionNumber);
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId || !Number.isFinite(targetSection) || targetSection <= 0) {
      setError('Please select a section');
      return;
    }
    setError(null);
    setHistoryRestoreLoading({ type: 'section', pdfId: normalizedPdfId, sectionNumber: targetSection });
    try {
      const snapshot = await fetchPdfWorkflowState({ pdfId: normalizedPdfId, sectionNumber: targetSection });
      const savedPlan = snapshot?.pre_analysis_plan;
      if (!savedPlan?.plan) {
        throw new Error('No saved plan was found for this section. Run Analyze & Plan again.');
      }
      setSelectedSection(targetSection);
      restorePreAnalysisPlan(savedPlan, { sectionNumber: targetSection });
      setStatus('analysis_ready');
      setCurrentStep(3.5);
    } catch (err) {
      setError('Failed to open saved plan: ' + (err?.message || err));
    } finally {
      setHistoryRestoreLoading(null);
    }
  };

  const applyWorkflowSnapshot = async ({
    snapshot,
    pdf = null,
    sectionNumber = null,
    targetStep = null,
    markLoadedFromHistory = false,
  }) => {
    const selectedPdfId = String(snapshot?.pdf_id || pdf?.pdf_id || '').trim();
    if (!selectedPdfId) {
      throw new Error(`Invalid ${historySourceLabel} selected from history.`);
    }

    const selectedPdf = {
      ...((pdf && typeof pdf === 'object') ? pdf : {}),
      ...((snapshot?.pdf && typeof snapshot.pdf === 'object') ? snapshot.pdf : {}),
      pdf_id: selectedPdfId,
      brd_update_supported: snapshot?.brd_update_supported ?? snapshot?.pdf?.brd_update_supported ?? pdf?.brd_update_supported,
      brd_update_blocked_reason: snapshot?.brd_update_blocked_reason || snapshot?.pdf?.brd_update_blocked_reason || pdf?.brd_update_blocked_reason || '',
    };
    const sectionsPayload = snapshot?.sections || {};
    const restoredSections = Array.isArray(sectionsPayload?.sections) ? sectionsPayload.sections : [];
    const dashboardPayload = snapshot?.dashboard || null;
    requireMatchingApplication(
      selectedPdf?.application_id
      || dashboardPayload?.application_id
      || snapshot?.application_id
    );
    const resolvedSectionNumber = Number(sectionNumber);
    const hasSectionTarget = Number.isFinite(resolvedSectionNumber) && resolvedSectionNumber > 0;
    const latestArtifacts = hasSectionTarget ? snapshot?.latest_artifacts : null;
    const restoredFromHistory = Boolean(markLoadedFromHistory || wasHistoryLoadedBrd(selectedPdfId));
    if (restoredFromHistory) {
      rememberHistoryLoadedBrd(selectedPdfId);
    }

    setPdfId(selectedPdfId);
    setDocumentName(resolveDocumentName(selectedPdf, snapshot, sectionsPayload));
    setIsReprocessed(true);
    setShowPreviousPDFs(false);
    setSelectedPreviousPdf(selectedPdf);
    setBrdLoadedFromHistory(restoredFromHistory);
    setDashboardPdf(selectedPdf);
    applySectionsResponse(sectionsPayload);
    setDashboardData(dashboardPayload);
    setDashboardError(null);
    setSectionArtifactIndex(buildSectionArtifactIndex(dashboardPayload));
    setSelectedSection(hasSectionTarget ? resolvedSectionNumber : null);
    setProcessingResult(null);
    setEditedTestCases([]);
    setOriginalTestCases([]);
    setHistoryScriptsByCaseId({});
    setRunResults({});
    setApiResponse(null);
    setPreAnalysisPlan(null);
    setApprovedCounts({ positive_count: null, negative_count: null });
    setApprovedFocus([]);
    setApprovedGlobalConstraints([]);
    setApprovedTestCaseFieldConfig(null);
    setDgsConfig(null);
    setAutomationBaseUrl('');
    setAutomationBaseUrlError('');
    setBulkMode('single');
    setBulkSelectedSections([]);
    setBulkJob(null);
    setBulkItems([]);
    setBulkEvents([]);
    setBulkError(null);
    setQueuedSingleGeneration(null);
    setQueuedPreAnalysis(null);
    clearReusableSections();
    setError(null);
    if (selectedPdf?.application_id || dashboardPayload?.application_id) {
      setSelectedWorkspaceId(String(selectedPdf?.application_id || dashboardPayload.application_id));
    }
    setApprovalStatus(
      snapshot?.approval
        ? {
            ...snapshot.approval,
            approval_state: normalizeApprovalState(snapshot.approval.approval_state),
          }
        : {
            approval_required: false,
            approval_state: 'approved',
            rejection_reason: selectedPdf?.rejection_reason || null,
          }
    );
    setApprovalEvents(Array.isArray(snapshot?.approval_events) ? snapshot.approval_events : []);
    setBrdChangeAnalysis(snapshot?.change_analysis || (snapshot?.change_summary ? { summary: snapshot.change_summary } : null));
    setBrdUpdatePending(isPendingBrdUpdatePayload(snapshot));
    setBrdSharingStatus(snapshot?.sharing || null);
    setBrdSharingRequests(Array.isArray(snapshot?.sharing_requests) ? snapshot.sharing_requests : []);
    setAutomationBaseUrl(String(snapshot?.automation_base_url || selectedPdf?.automation_base_url || '').trim());
    setAutomationBaseUrlError('');
    setProcessingMode('pdf-only');

    if (!hasSectionTarget) {
      const normalizedStep = normalizePdfWorkflowStep(targetStep, restoredSections.length > 0 ? 3 : 2);
      const shouldOfferReusableSections = restoredSections.length > 0 && normalizedStep === 2;
      if (shouldOfferReusableSections) {
        setReusableSections({
          available: true,
          stale: false,
          count: restoredSections.length,
          extractionMethod: sectionsPayload?.extraction_method || '',
          itemType: sectionsPayload?.item_type || '',
        });
      }
      setStatus(shouldOfferReusableSections ? 'uploaded' : restoredSections.length > 0 ? 'sections_ready' : 'uploaded');
      setCurrentStep(normalizedStep);
      return { pdfId: selectedPdfId, sections: restoredSections };
    }

    if (normalizePdfWorkflowStep(targetStep, 5) === 3.5) {
      const persistedPlan = snapshot?.pre_analysis_plan;
      if (persistedPlan?.plan) {
        restorePreAnalysisPlan(persistedPlan, { sectionNumber: resolvedSectionNumber });
      }
      setStatus('sections_ready');
      setCurrentStep(3.5);
      return {
        pdfId: selectedPdfId,
        sectionNumber: resolvedSectionNumber,
        preAnalysisPlan: persistedPlan?.plan ? persistedPlan : null,
      };
    }

    if (!latestArtifacts) {
      throw sectionOutputUnavailableError('No saved section state found for this route.');
    }

    const draftTestPayload = latestArtifacts?.test_cases?.draft?.payload;
    const activeTestPayload = latestArtifacts?.test_cases?.active?.payload;
    let latestTestPayload = draftTestPayload || activeTestPayload;
    let normalizedTestCases = normalizeHistoryTestCasesPayload(latestTestPayload);
    if (!normalizedTestCases && Number(latestArtifacts?.test_cases?.count || 0) > 0) {
      const versionsResponse = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.SECTION_ARTIFACTS(selectedPdfId, resolvedSectionNumber)}`);
      if (versionsResponse.ok) {
        const versionsPayload = await versionsResponse.json().catch(() => ({}));
        const savedVersion = (Array.isArray(versionsPayload?.versions) ? versionsPayload.versions : [])
          .find((version) => normalizeHistoryTestCasesPayload(version?.payload));
        latestTestPayload = savedVersion?.payload || latestTestPayload;
        normalizedTestCases = normalizeHistoryTestCasesPayload(latestTestPayload);
      }
    }
    const normalizedSavedBaseline = normalizeHistoryTestCasesPayload(activeTestPayload) || normalizedTestCases;
    const draftTestArtifact = latestArtifacts?.test_cases?.draft || null;
    const activeTestArtifact = latestArtifacts?.test_cases?.active || null;
    const restoredRequestPayload =
      draftTestArtifact?.request_payload && typeof draftTestArtifact.request_payload === 'object'
        ? draftTestArtifact.request_payload
        : (
            activeTestArtifact?.request_payload && typeof activeTestArtifact.request_payload === 'object'
              ? activeTestArtifact.request_payload
              : {}
          );
    const restoredFocus = normalizeUniqueStringList(
      restoredRequestPayload?.additional_focus
      ?? draftTestArtifact?.approved_focus
      ?? activeTestArtifact?.approved_focus
    );
    const restoredGlobalConstraints = normalizeUniqueStringList(
      restoredRequestPayload?.global_constraints
      ?? draftTestArtifact?.approved_global_constraints
      ?? activeTestArtifact?.approved_global_constraints
    );
    const restoredFieldConfig =
      restoredRequestPayload?.test_case_field_config
      || latestTestPayload?.field_config
      || null;
    if (!normalizedTestCases) {
      throw sectionOutputUnavailableError(
        Number(latestArtifacts?.test_cases?.count || 0) > 0
          ? 'Saved test-case versions exist, but none contain readable test cases. Regenerate this section to create a new output.'
          : 'This section has no generated test cases yet. Generate test cases for it from section selection.'
      );
    }

    setApprovedFocus(restoredFocus);
    setApprovedGlobalConstraints(restoredGlobalConstraints);
    setApprovedTestCaseFieldConfig(restoredFieldConfig);
    const processingPayload = {
      ...buildHistoryProcessingResult({
        latestArtifacts,
        normalizedTestCases,
        selectedPdfId,
        selectedSectionNumber: resolvedSectionNumber,
      }),
      approved_focus: restoredFocus,
      approved_global_constraints: restoredGlobalConstraints,
      test_case_field_config: restoredFieldConfig,
    };
    setProcessingResult(processingPayload);

    const restoredCases = extractTestCases({ test_cases: normalizedTestCases });
    const baselineCases = extractTestCases({ test_cases: normalizedSavedBaseline });
    setOriginalTestCases(baselineCases);
    setEditedTestCases(restoredCases);
    setHistoryScriptsByCaseId(buildHistoryScriptsMap(latestArtifacts?.scripts));

    if (snapshot?.test_case_approval) {
      setTestCaseApprovalStatus({
        ...snapshot.test_case_approval,
        approval_state: normalizeTestCaseApprovalState(snapshot.test_case_approval.approval_state),
        section_number: Number(snapshot.test_case_approval.section_number || resolvedSectionNumber),
      });
    }
    setTestCaseApprovalEvents(
      Array.isArray(snapshot?.test_case_approval_events)
        ? snapshot.test_case_approval_events
        : []
    );
    setStatus('completed');
    setCurrentStep(normalizePdfWorkflowStep(targetStep, 5));
    return { pdfId: selectedPdfId, sectionNumber: resolvedSectionNumber };
  };

  const hydratePdfWorkflow = async ({
    pdf = null,
    pdfId: targetPdfId = null,
    sectionNumber = null,
    targetStep = null,
    markLoadedFromHistory = false,
    // Lets a superseded caller (e.g. a route effect that was cleaned up) skip
    // applying its snapshot over the state of the hydration that replaced it.
    isStale = null,
  } = {}) => {
    const selectedPdfId = String(targetPdfId || pdf?.pdf_id || '').trim();
    if (!selectedPdfId) {
      throw new Error(`Invalid ${historySourceLabel} selected from history.`);
    }
    setStatus('extracting');
    pdfHydrationsInFlightRef.current += 1;
    try {
      let resolvedSectionNumber = sectionNumber;
      let resolvedTargetStep = targetStep;
      let snapshot = await fetchPdfWorkflowState({
        pdfId: selectedPdfId,
        sectionNumber: resolvedSectionNumber,
      });
      const restoredSourceType = String(
        snapshot?.pdf?.source_type
        || snapshot?.source_type
        || pdf?.source_type
        || pdf?.item_type
        || ''
      ).trim().toLowerCase();
      if (restoredSourceType === 'testcase_excel' && !Number(resolvedSectionNumber)) {
        const restoredSections = Array.isArray(snapshot?.sections?.sections)
          ? snapshot.sections.sections
          : [];
        const firstSection = restoredSections[0] || {};
        resolvedSectionNumber = Number(
          firstSection.section_number || firstSection.number || 1
        );
        resolvedTargetStep = 5;
        snapshot = await fetchPdfWorkflowState({
          pdfId: selectedPdfId,
          sectionNumber: resolvedSectionNumber,
        });
      }
      if (typeof isStale === 'function' && isStale()) return null;
      return await applyWorkflowSnapshot({
        snapshot,
        pdf,
        sectionNumber: resolvedSectionNumber,
        targetStep: resolvedTargetStep,
        markLoadedFromHistory,
      });
    } catch (err) {
      if (!(typeof isStale === 'function' && isStale())) {
        setStatus(err?.pdfRestored ? 'sections_ready' : 'idle');
      }
      throw err;
    } finally {
      pdfHydrationsInFlightRef.current -= 1;
    }
  };

  const loadPreviousPdf = async (pdf) => {
    const targetPdfId = String(pdf?.pdf_id || '').trim();
    setHistoryRestoreLoading({
      type: 'pdf',
      pdfId: targetPdfId,
    });
    setError(null);
    rememberHistoryLoadedBrd(targetPdfId);
    try {
      const sourceType = String(pdf?.source_type || pdf?.item_type || '').trim().toLowerCase();
      await hydratePdfWorkflow({
        pdf,
        sectionNumber: sourceType === 'testcase_excel' ? 1 : null,
        targetStep: sourceType === 'testcase_excel' ? 5 : 2,
        markLoadedFromHistory: true,
      });
    } catch (err) {
      clearHistoryLoadedBrd(targetPdfId);
      setError(`Failed to load ${historySourceLabel}: ` + err.message);
    } finally {
      setHistoryRestoreLoading(null);
    }
  };

  const deletePreviousPdf = async (pdf) => {
    const targetPdfId = String(pdf?.pdf_id || '').trim();
    if (!targetPdfId) {
      throw new Error(`Invalid ${historySourceLabel} selected for deletion.`);
    }

    const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.DELETE_PROCESSED_PDF(targetPdfId)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to delete ${historySourceLabel} from history`);
    }
    const payload = await response.json().catch(() => ({}));

    const updatedPdfs = previousPDFs.filter((row) => row?.pdf_id !== targetPdfId);
    setPreviousPDFs(updatedPdfs);

    if (selectedPreviousPdf?.pdf_id === targetPdfId) {
      setSelectedPreviousPdf(null);
    }

    if (dashboardPdf?.pdf_id === targetPdfId) {
      const nextDashboard = updatedPdfs[0] || null;
      setDashboardPdf(nextDashboard);
      if (nextDashboard?.pdf_id) {
        if (nextDashboard?.application_id) {
          setSelectedWorkspaceId(String(nextDashboard.application_id));
        }
        const dashboardLoaders = [loadPdfArtifactsDashboard(nextDashboard.pdf_id)];
        if (historySourceCategory === 'brd') {
          dashboardLoaders.push(loadBrdSharingForPdf(nextDashboard.pdf_id));
        }
        await Promise.all(dashboardLoaders);
      } else {
        setDashboardData(null);
        setDashboardError(null);
        setBrdSharingStatus(null);
        setBrdSharingRequests([]);
      }
    }

    if (pdfId === targetPdfId) {
      setPdfId(null);
      setFile(null);
      setDocumentName('');
      setSections([]);
      setRawSectionOutline([]);
      setSectionGrouping(null);
      setSelectedSection(null);
      setProcessingResult(null);
      setEditedTestCases([]);
      setOriginalTestCases([]);
      setHistoryScriptsByCaseId({});
      setSectionArtifactIndex({});
      setRunResults({});
      setApiResponse(null);
      setPreAnalysisPlan(null);
      setApprovedCounts({ positive_count: null, negative_count: null });
      setApprovedFocus([]);
      setApprovedGlobalConstraints([]);
      setApprovedTestCaseFieldConfig(null);
      setDgsConfig(null);
      setAutomationBaseUrl('');
      setAutomationBaseUrlError('');
      setConfirmedReplacementSection(null);
      setBulkMode('single');
      setBulkSelectedSections([]);
      setBulkJob(null);
      setBulkItems([]);
      setBulkEvents([]);
      setBulkError(null);
      setQueuedSingleGeneration(null);
      setQueuedPreAnalysis(null);
      setStatus('idle');
      setCurrentStep(1);
      resetProcessingMode();
    }

    return payload;
  };

  const openSectionFromHistory = async ({ pdf, sectionNumber, targetStep = 5 }) => {
    const selectedPdf =
      pdf ||
      selectedPreviousPdf ||
      dashboardPdf ||
      (pdfId
        ? {
            pdf_id: pdfId,
            pdf_file: documentName || file?.name || 'Current BRD',
          }
        : null);
    const selectedPdfId = selectedPdf?.pdf_id || pdfId;
    const selectedSectionNumber = Number(sectionNumber);

    if (!selectedPdfId) {
      setError('Please select a BRD from history first.');
      return;
    }
    if (!Number.isFinite(selectedSectionNumber) || selectedSectionNumber <= 0) {
      setError('Invalid section selected from history.');
      return;
    }

    setError(null);
    setHistoryRestoreLoading({
      type: 'section',
      pdfId: String(selectedPdfId || '').trim(),
      sectionNumber: selectedSectionNumber,
    });
    rememberHistoryLoadedBrd(selectedPdfId);
    try {
      await hydratePdfWorkflow({
        pdf: selectedPdf,
        pdfId: selectedPdfId,
        sectionNumber: selectedSectionNumber,
        targetStep,
        markLoadedFromHistory: true,
      });
      setCurrentStep(targetStep);
    } catch (err) {
      clearHistoryLoadedBrd(selectedPdfId);
      setError('Failed to open section from history: ' + err.message);
      setStatus('sections_ready');
      setCurrentStep(3);
    } finally {
      setHistoryRestoreLoading(null);
    }
  };

  useEffect(() => {
    if (!queuedSingleGeneration || queuedSingleGeneration.opening) return;

    const queuedJobId = String(queuedSingleGeneration.bulk_job_id || '').trim();
    const activeJobId = String(bulkJob?.id || '').trim();
    if (!queuedJobId || activeJobId !== queuedJobId) return;

    const jobStatus = normalizeBulkStatus(bulkJob?.status);
    if (!isBulkJobTerminal(jobStatus)) return;

    const queuedSectionNumber = Number(queuedSingleGeneration.section_number);
    const queuedItem = (Array.isArray(bulkItems) ? bulkItems : []).find(
      (item) => Number(item?.section_number) === queuedSectionNumber
    );
    if (!queuedItem) return;

    const itemStatus = normalizeBulkStatus(queuedItem?.status);

    if (jobStatus !== 'completed' || itemStatus !== 'completed') {
      setQueuedSingleGeneration(null);
      setBulkError(queuedItem?.error || 'Section generation did not complete successfully.');
      setStatus('sections_ready');
      return;
    }

    setQueuedSingleGeneration((prev) =>
      prev && String(prev.bulk_job_id || '').trim() === queuedJobId
        ? { ...prev, opening: true }
        : prev
    );

    void (async () => {
      try {
        await loadPdfArtifactsDashboard(queuedSingleGeneration.pdf_id || pdfId, {
          updateSectionIndex: true,
        });
      } catch (err) {
        setError('Failed to refresh generated sections: ' + (err?.message || err));
      } finally {
        setQueuedSingleGeneration(null);
      }
    })();
    // The loaders are intentionally omitted so this effect reacts to queue state,
    // not to function identity churn from this large workflow hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bulkItems,
    bulkJob,
    isBulkJobTerminal,
    normalizeBulkStatus,
    pdfId,
    queuedSingleGeneration,
  ]);

  useEffect(() => {
    if (!queuedPreAnalysis || queuedPreAnalysis.applying) return;

    const queuedJobId = String(queuedPreAnalysis.bulk_job_id || '').trim();
    const activeJobId = String(bulkJob?.id || '').trim();
    if (!queuedJobId || activeJobId !== queuedJobId) return;

    const jobStatus = normalizeBulkStatus(bulkJob?.status);
    if (!isBulkJobTerminal(jobStatus)) return;

    const queuedSectionNumber = Number(queuedPreAnalysis.section_number);
    const queuedItem = (Array.isArray(bulkItems) ? bulkItems : []).find(
      (item) => Number(item?.section_number) === queuedSectionNumber
    );
    if (!queuedItem) return;

    const itemStatus = normalizeBulkStatus(queuedItem?.status);
    if (jobStatus !== 'completed' || itemStatus !== 'completed') {
      const message = queuedItem?.error?.message || queuedItem?.error || 'Pre-analysis did not complete successfully.';
      setQueuedPreAnalysis(null);
      setConfirmedReplacementSection(null);
      setBulkError(message);
      setError('Failed to pre-analyze section: ' + message);
      setStatus('sections_ready');
      return;
    }

    const data = queuedItem?.result_payload || queuedItem?.result_summary;
    if (!data?.plan) {
      const message = 'Analysis completed without a plan.';
      setQueuedPreAnalysis(null);
      setConfirmedReplacementSection(null);
      setBulkError(message);
      setError('Failed to pre-analyze section: ' + message);
      setStatus('sections_ready');
      return;
    }

    setQueuedPreAnalysis((prev) =>
      prev && String(prev.bulk_job_id || '').trim() === queuedJobId
        ? { ...prev, applying: true }
        : prev
    );

    void (async () => {
      try {
        if (queuedPreAnalysis.replace_existing && queuedPreAnalysis.had_existing_artifacts) {
          await loadPdfArtifactsDashboard(queuedPreAnalysis.pdf_id || pdfId, {
            updateSectionIndex: true,
          });
        }

        const suggested = data?.plan?.suggested || {};
        setPreAnalysisPlan(data);
        setApprovedCounts({
          positive_count: suggested.positive_count ?? 20,
          negative_count: suggested.negative_count ?? 12,
        });
        setApprovedFocus(
          Array.isArray(data?.plan?.coverage_focus)
            ? normalizeUniqueStringList(data.plan.coverage_focus)
            : []
        );
        setApprovedGlobalConstraints(
          normalizeUniqueStringList(
            data?.approved_global_constraints
            ?? data?.plan?.approved_global_constraints
            ?? queuedPreAnalysis.global_constraints
            ?? []
          )
        );
        // Keep the section list in step with the persisted plan, so going Back
        // shows this section as analyzed without reloading the dashboard.
        // Sections with test cases keep showing those (see buildSectionArtifactIndex).
        setSectionArtifactIndex((prev) => (
          prev?.[queuedSectionNumber]?.has_generated
            ? prev
            : {
              ...prev,
              [queuedSectionNumber]: {
                section_number: queuedSectionNumber,
                has_generated: false,
                test_case_versions: 0,
                script_versions: 0,
                ...prev?.[queuedSectionNumber],
                pending_plan: {
                  version: null,
                  created_at: new Date().toISOString(),
                  suggested,
                },
              },
            }
        ));
        setStatus('analysis_ready');
        setCurrentStep(3.5);
        await fetchApprovalStatus(queuedPreAnalysis.pdf_id || pdfId);
      } catch (err) {
        setError('Failed to apply analysis plan: ' + (err?.message || err));
        setStatus('sections_ready');
      } finally {
        setQueuedPreAnalysis(null);
      }
    })();
    // The loaders are intentionally omitted so this effect reacts to queue state,
    // not to function identity churn from this large workflow hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bulkItems,
    bulkJob,
    isBulkJobTerminal,
    normalizeBulkStatus,
    normalizeUniqueStringList,
    pdfId,
    queuedPreAnalysis,
  ]);

  const buildArtifactSavePayload = (testCases, baselinePayload = null, options = {}) => {
    const autosave = Boolean(options?.autosave);
    const manualImportMeta =
      options?.manualImportMeta && typeof options.manualImportMeta === 'object'
        ? options.manualImportMeta
        : null;
    let editedPayload;
    if (hasCategorizedTestCases(processingResult?.test_cases)) {
      editedPayload = {
        positive: testCases?.positive || [],
        negative: testCases?.negative || [],
      };
    } else {
      editedPayload = Array.isArray(testCases)
        ? testCases.map(({ _category, ...rest }) => rest)
        : testCases;
    }

    return {
      edited_test_cases: editedPayload,
      section_title: processingResult?.section_title || 'Test Cases',
      section_number: processingResult?.section_number,
      pdf_id: pdfId,
      ...(manualImportMeta ? { manual_import: manualImportMeta } : {}),
      ...(autosave
        ? {}
        : {
            original_test_cases: baselinePayload ?? originalTestCases,
            comparison: {
              original_count: getTestCaseCount(baselinePayload ?? originalTestCases),
              edited_count: getTestCaseCount(testCases),
              timestamp: new Date().toISOString(),
            },
          }),
    };
  };

  const exportTestCasesToExcel = async ({
    testCases,
    baselinePayload = null,
    filename = null,
    comparison = null,
  }) => {
    const payload = {
      ...buildArtifactSavePayload(testCases, baselinePayload),
      filename,
      testing_type: testingType,
      ...(comparison ? { comparison } : {}),
    };

    const response = await apiFetch(`${API_ENDPOINTS.SAVE_TEST_CASES}/${pdfId}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to export test cases');
    }

    const data = await response.json();
    if (data?.job?.id) announceJob(data.job);
    return data;
  };

  /**
   * Save test cases to backend.
   * Manual save also prepares the Excel export used by the next step.
   */
  const handleSaveTestCases = async (testCases, baselinePayload = null, options = {}) => {
    const autosave = Boolean(options?.autosave);
    if (!autosave) {
      setStatus('saving');
    }
    setError(null);

    try {
      const comparisonMetrics = autosave
        ? null
        : buildTestCaseComparisonMetrics(testCases, baselinePayload ?? originalTestCases);
      const artifactPayload = {
        ...buildArtifactSavePayload(testCases, baselinePayload, {
          autosave,
          manualImportMeta: options?.manualImportMeta || null,
        }),
        ...(comparisonMetrics ? { comparison: comparisonMetrics } : {}),
        save_mode: autosave ? 'autosave' : 'manual',
      };

      const persistResponse = await apiFetch(API_ENDPOINTS.SAVE_TEST_CASE_ARTIFACTS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(artifactPayload),
        keepalive: Boolean(options?.keepalive),
      });

      if (!persistResponse.ok) {
        const errorData = await persistResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save test cases');
      }

      const persistData = await persistResponse.json();
      setEditedTestCases(testCases);
      if (!autosave && baselinePayload) {
        setOriginalTestCases(baselinePayload);
      }
      if (persistData?.test_case_approval) {
        setTestCaseApprovalStatus({
          ...persistData.test_case_approval,
          approval_state: normalizeTestCaseApprovalState(
            persistData.test_case_approval.approval_state
          ),
        });
        const updatedSectionNumber = Number(
          persistData?.test_case_approval?.section_number
            || persistData?.section_number
            || processingResult?.section_number
            || selectedSection
            || 0
        );
        if (Number.isFinite(updatedSectionNumber) && updatedSectionNumber > 0) {
          fetchTestCaseApprovalEvents(pdfId, updatedSectionNumber).catch(() => {});
        }
      }
      const persistedArtifactMeta = persistData?.artifact || persistData?.draft || null;
      if (persistedArtifactMeta && typeof persistedArtifactMeta === 'object') {
        setProcessingResult((prev) => {
          if (!prev || typeof prev !== 'object') return prev;
          const editedByName = persistedArtifactMeta?.edited_by_name || prev.edited_by_name || null;
          const editedByEmail = persistedArtifactMeta?.edited_by_email || prev.edited_by_email || null;
          const editedBy =
            persistedArtifactMeta?.edited_by
            || editedByName
            || editedByEmail
            || prev.edited_by
            || null;
          const editedAt = persistedArtifactMeta?.updated_at || prev.edited_at || null;
          return {
            ...prev,
            test_cases: persistData?.test_cases || testCases,
            artifact: persistData?.artifact || prev.artifact,
            manual_import: artifactPayload?.manual_import || prev.manual_import || null,
            edited_at: editedAt,
            edited_by: editedBy,
            edited_by_name: editedByName,
            edited_by_email: editedByEmail,
          };
        });
      }

      if (autosave) {
        return persistData;
      }

      const exportData = await exportTestCasesToExcel({
        testCases,
        baselinePayload,
        comparison: comparisonMetrics || persistData?.metrics || null,
      });

      setStatus('saved');
      setCurrentStep(6);
      setApiResponse({
        ...exportData,
        metrics: comparisonMetrics || exportData?.metrics,
      });
      return {
        ...persistData,
        export: exportData,
      };
    } catch (err) {
      setError('Failed to save test cases: ' + err.message);
      if (!autosave) {
        setStatus('completed');
      }
      throw err;
    }
  };

  const buildExecutionAugmentedCases = (testCases, results) => {
    if (!testCases) return testCases;

    const addExecution = (cases, category) =>
      (cases || []).map((tc, idx) => {
        const caseKey = `${category}::${tc.test_case_no || 'case'}::${idx}`;
        const run = results?.[caseKey];
        return {
          ...tc,
          execution_status: run ? (run.exit_code === 0 ? 'Passed' : 'Failed') : 'Not Run'
        };
      });

    if (hasCategorizedTestCases(testCases)) {
      return {
        positive: addExecution(testCases.positive, 'positive'),
        negative: addExecution(testCases.negative, 'negative')
      };
    }

    if (Array.isArray(testCases)) {
      return testCases.map((tc, idx) => {
        const caseKey = `all::${tc.test_case_no || 'case'}::${idx}`;
        const run = results?.[caseKey];
        return {
          ...tc,
          execution_status: run ? (run.exit_code === 0 ? 'Passed' : 'Failed') : 'Not Run'
        };
      });
    }

    return testCases;
  };

  const exportExecutionResults = async () => {
    setStatus('saving');
    setError(null);

    try {
      const augmented = buildExecutionAugmentedCases(editedTestCases, runResults);

      let testCasesPayload;
      if (hasCategorizedTestCases(processingResult?.test_cases)) {
        testCasesPayload = {
          positive: augmented.positive,
          negative: augmented.negative
        };
      } else if (Array.isArray(augmented)) {
        testCasesPayload = augmented.map(({ _category, _case_key, ...rest }) => rest);
      } else {
        testCasesPayload = augmented;
      }

      const data = await exportTestCasesToExcel({
        testCases: testCasesPayload,
        baselinePayload: originalTestCases,
        filename: 'TestCases_Execution.xlsx',
      });
      setStatus('saved');
      return data;
    } catch (err) {
      setError('Failed to export execution report: ' + err.message);
      setStatus('completed');
      throw err;
    }
  };

  const openWorkspaceMembersManager = async () => {
    if (!selectedWorkspaceId) {
      setWorkspaceError('Select an application first.');
      return;
    }
    setShowWorkspaceMembersModal(true);
    await loadWorkspaceMembers(selectedWorkspaceId);
  };

  const resetWorkflow = () => {
    const pendingExcelImportId = String(excelImportAnalysis?.import_id || '').trim();
    if (pendingExcelImportId && currentStep === 1.5) {
      apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.TEST_CASE_IMPORT_CANCEL(pendingExcelImportId)}`,
        { method: 'DELETE' }
      ).catch(() => {});
    }
    clearHistoryLoadedBrd();
    getSessionStorage()?.removeItem('active_pdf_id');
    getSessionStorage()?.removeItem(activePdfSessionKey);
    getSessionStorage()?.removeItem(workflowLastRouteSessionKey);
    getSessionStorage()?.removeItem(excelImportSessionKey);
    setFile(null);
    setOpenApiFile(null);
    setExcelFile(null);
    setExcelImportAnalysis(null);
    setDocumentName('');
    setManualInputTitle('');
    setManualInputText('');
    setManualInputImages([]);
    setPdfId(null);
    setSections([]);
    setRawSectionOutline([]);
    setSectionGrouping(null);
    setSelectedSection(null);
    setStatus('idle');
    setError(null);
    setProcessingResult(null);
    setCurrentStep(1);
    setIsReprocessed(false);
    setShowPreviousPDFs(false);
    setPreviousPDFsLoading(false);
    setPreviousPDFsError(null);
    setHistoryRestoreLoading(null);
    setDashboardPdf(null);
    setDashboardData(null);
    setDashboardLoading(false);
    setDashboardError(null);
    setSectionArtifactIndex({});
    setHistoryScriptsByCaseId({});
    setEditedTestCases([]);
    setOriginalTestCases([]);
    setApiResponse(null);
    setRunResults({});
    setPreAnalysisPlan(null);
    setApprovedCounts({ positive_count: null, negative_count: null });
    setApprovedFocus([]);
    setApprovedGlobalConstraints([]);
    setApprovedTestCaseFieldConfig(null);
    setDgsConfig(null);
    setAutomationBaseUrl('');
    setAutomationBaseUrlLoading(false);
    setAutomationBaseUrlSaving(false);
    setAutomationBaseUrlError('');
    setConfirmedReplacementSection(null);
    setApprovalStatus(null);
    setApprovalEvents([]);
    setApprovalLoading(false);
    setBrdSharingStatus(null);
    setBrdSharingRequests([]);
    setBrdSharingLoading(false);
    setTestCaseApprovalStatus(null);
    setTestCaseApprovalEvents([]);
    setTestCaseApprovalLoading(false);
    setBulkMode('single');
    setBulkSelectedSections([]);
    setBulkJob(null);
    setBulkItems([]);
    setBulkEvents([]);
    setBulkLoading(false);
    setBulkError(null);
    setQueuedSingleGeneration(null);
    setQueuedPreAnalysis(null);
    setBrdLoadedFromHistory(false);
    setBrdChangeAnalysis(null);
    setBrdUpdatePending(false);
    resetProcessingMode();
  };

  const handleEditTestCasesComplete = (editedCases) => {
    setEditedTestCases(editedCases);
    // Note: Save happens in EditTestCasesStep via onSaveTestCases
  };

  return {
    file,
    setFile,
    openApiFile,
    setOpenApiFile,
    excelFile,
    setExcelFile,
    excelImportAnalysis,
    setExcelImportAnalysis,
    workflowRouteRoot,
    activePdfSessionKey,
    workflowLastRouteSessionKey,
    excelImportSessionKey,
    documentName,
    setDocumentName,
    manualInputTitle,
    setManualInputTitle,
    manualInputText,
    setManualInputText,
    manualInputImages,
    setManualInputImages,
    pdfId,
    setPdfId,
    sections,
    setSections,
    rawSectionOutline,
    sectionGrouping,
    selectedSection,
    setSelectedSection,
    bulkMode,
    setBulkMode,
    bulkSelectedSections,
    setBulkSelectedSections,
    bulkJob,
    setBulkJob,
    bulkItems,
    setBulkItems,
    bulkEvents,
    setBulkEvents,
    bulkLoading,
    setBulkLoading,
    bulkError,
    setBulkError,
    pdfExtractionMode,
    setPdfExtractionMode: handlePdfExtractionModeChange,
    brdLoadedFromHistory,
    brdChangeAnalysis,
    brdUpdatePending,
    setBrdChangeAnalysis,
    reusableSections,
    isOpenApiSource,
    isExcelSource,
    isBulkJobTerminal,
    status,
    setStatus,
    activeUploadSource,
    error,
    setError,
    workspaces,
    workspaceLoading,
    workspaceError,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    applicationLocked,
    lockedApplicationId,
    selectedWorkspace,
    canCreateWorkspace,
    canViewWorkspaceMembers,
    workspaceMembers,
    workspaceMembersLoading,
    workspaceMembersError,
    activeUsers,
    activeUsersLoading,
    activeUsersError,
    showWorkspaceMembersModal,
    setShowWorkspaceMembersModal,
    approvalStatus,
    approvalEvents,
    approvalLoading,
    brdSharingStatus,
    brdSharingRequests,
    brdSharingLoading,
    testCaseApprovalStatus,
    testCaseApprovalEvents,
    testCaseApprovalLoading,
    pendingApprovals,
    pendingApprovalsLoading,
    canManageWorkspaceMembers,
    canRequestApproval,
    canApproveApproval,
    canRequestBrdSharing,
    canApproveBrdSharing,
    canRequestTestCaseApproval,
    canApproveTestCaseApproval,
    isTestCaseApprovedForCurrentSection,
    canViewApprovalQueue,
    processingResult,
    setProcessingResult,
    currentStep,
    setCurrentStep,
    testingType,
    setTestingType,
    isReprocessed,
    setIsReprocessed,
    showPreviousPDFs,
    setShowPreviousPDFs,
    previousPDFs,
    setPreviousPDFs,
    previousPDFsLoading,
    previousPDFsError,
    historyRestoreLoading,
    selectedPreviousPdf,
    setSelectedPreviousPdf,
    dashboardPdf,
    setDashboardPdf,
    dashboardData,
    setDashboardData,
    dashboardLoading,
    setDashboardLoading,
    dashboardError,
    setDashboardError,
    sectionArtifactIndex,
    historyScriptsByCaseId,
    processingMode,
    setProcessingMode,
    handleFileChange,
    handleOpenApiFileChange,
    handleExcelFileChange,
    handleManualInputImagesChange,
    uploadPDF,
    analyzeExcelTestCases,
    restoreExcelTestCaseImport,
    previewExcelTestCases,
    commitExcelTestCases,
    cancelExcelTestCaseImport,
    uploadUpdatedBrdVersion,
    uploadTextImages,
    uploadOpenApi,
    extractSections,
    restoreSkippedTocSections,
    removeRestoredTocSections,
    applyRebuiltToc,
    useReusableSections,
    returnToExtractionStep,
    processSection,
    generateAdditionalTestCases,
    preAnalyzeSection,
    openSavedPreAnalysisPlan,
    restorePreAnalysisPlan,
    startBulkGeneration,
    cancelBulkGeneration,
    retryFailedGenerationSections,
    fetchBulkGenerationSnapshot,
    restoreBulkGenerationById,
    restoreLatestBulkGeneration,
    toggleBulkSectionSelection,
    selectAllBulkSections,
    clearBulkSelection,
    loadPreviousPDFs,
    loadPdfArtifactsDashboard,
    hydratePdfWorkflow,
    previewPreviousPdf,
    loadPreviousPdf,
    deletePreviousPdf,
    openSectionFromHistory,
    resetWorkflow,
    editedTestCases,
    setEditedTestCases,
    originalTestCases,
    setOriginalTestCases,
    handleEditTestCasesComplete,
    handleSaveTestCases,
    exportExecutionResults,
    setApiResponse,
    apiResponse,
    runResults,
    setRunResults,
    preAnalysisPlan,
    setPreAnalysisPlan,
    approvedCounts,
    setApprovedCounts,
    approvedFocus,
    setApprovedFocus,
    approvedGlobalConstraints,
    setApprovedGlobalConstraints,
    approvedTestCaseFieldConfig,
    setApprovedTestCaseFieldConfig,
    dgsConfig,
    setDgsConfig,
    automationBaseUrl,
    setAutomationBaseUrl,
    automationBaseUrlLoading,
    automationBaseUrlSaving,
    automationBaseUrlError,
    fetchAutomationBaseUrl,
    updateAutomationBaseUrl,
    parseDgsSheet,
    downloadDgsTemplate,
    downloadExtractedSectionsJson,
    clearDgsConfig,
    openUploadedPdf,
    confirmedReplacementSection,
    setConfirmedReplacementSection,
    loadWorkspaces,
    createWorkspace,
    loadWorkspaceMembers,
    loadActiveUsers,
    addWorkspaceMember,
    updateWorkspaceMemberRole,
    removeWorkspaceMember,
    openWorkspaceMembersManager,
    requestBrdApproval,
    remindBrdApproval,
    approveBrd,
    rejectBrd,
    requestBrdShare,
    approveBrdShareRequest,
    rejectBrdShareRequest,
    revokeBrdShareAccess,
    requestTestCaseApproval,
    remindTestCaseApproval,
    approveTestCaseApproval,
    rejectTestCaseApproval,
    fetchApprovalStatus,
    fetchApprovalEvents,
    fetchBrdSharingStatus,
    fetchBrdSharingRequests,
    loadBrdSharingForPdf,
    fetchTestCaseApprovalStatus,
    fetchTestCaseApprovalEvents,
    loadPendingApprovals,
  };
};
